import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../lib/firebase';
import { supabase } from '../../lib/supabaseClient';
import { onAuthStateChanged } from 'firebase/auth';
import { notificationService } from '../../services/notificationService';
import { AdsPromoteButton, MyAdsButton } from '../ads/Ads';

import {
  Bell,
  ShoppingCart,
  Heart,
  Package,
  Send,
  ShoppingBag,
  Store,
  RefreshCw,
  ChevronRight,
  Eye,
  EyeOff,
  Star,
  MessageSquare,
  History,
  Users,
  Database,
  Gift,
  User,
  Settings,
  HelpCircle,
  CheckCheck,
  Truck,
  Wifi,
  Zap,
  Smartphone,
  Trophy,
  HandHeart,
  Tv,
  Camera,
  Music,
  Coffee,
  BookOpen,
  Wallet,
  Clock,
  Phone,
  XCircle,
  CheckCircle,
  AlertTriangle,
  Info,
  Star as StarIcon,
  MessageCircle,
  PhoneCall,
  X,
  Loader,
  CreditCard,
  TrendingUp,
  Shield,
  Globe,
  Home,
  Plus,
  MapPin,
  UtensilsCrossed,
  Landmark,
  Plane,
  Hotel,
  HardHat,
  Truck as TruckIcon,
  Briefcase,
  Ship,
  Car,
  Building2,
  Umbrella,
  Handshake
} from 'lucide-react';
import './UserDashboard.css';

// ========== IMAGE TRANSFORM HELPERS ==========
const getThumbnailUrl = (originalUrl: string, width = 400, quality = 65): string => {
  if (!originalUrl) return originalUrl;
  if (!originalUrl.includes('/storage/v1/object/public/')) return originalUrl;
  const transformed = originalUrl.replace(
    '/storage/v1/object/public/',
    '/storage/v1/render/image/public/'
  );
  const sep = transformed.includes('?') ? '&' : '?';
  return `${transformed}${sep}width=${width}&quality=${quality}&resize=cover`;
};

const getOriginalUrl = (url: string): string => {
  if (!url) return url;
  if (url.includes('/storage/v1/render/image/public/')) {
    const cleaned = url.replace('/storage/v1/render/image/public/', '/storage/v1/object/public/');
    return cleaned.split('?')[0];
  }
  return url;
};
// ========== END IMAGE HELPERS ==========

// ========== NOTIFICATION FUNCTIONS ==========
const getVendorDetails = async (vendorId: string): Promise<{ firebaseUid: string | null; businessEmail: string | null; shopName: string | null }> => {
  try {
    const { data: vendorProfile, error: vendorError } = await supabase
      .from('vendor_profiles')
      .select('user_id, business_email, shop_name')
      .eq('vendor_id', vendorId)
      .maybeSingle();

    if (vendorError) {
      console.error('❌ Error fetching vendor profile:', vendorError);
    }

    if (vendorProfile) {
      return {
        firebaseUid: vendorProfile.user_id,
        businessEmail: vendorProfile.business_email,
        shopName: vendorProfile.shop_name || 'Shop'
      };
    }

    return { firebaseUid: null, businessEmail: null, shopName: null };
  } catch (error) {
    console.error('❌ Error in getVendorDetails:', error);
    return { firebaseUid: null, businessEmail: null, shopName: null };
  }
};

const getCurrentUserName = async (): Promise<string> => {
  const user = auth.currentUser;
  if (!user) return 'A user';

  try {
    const { data } = await supabase
      .from('users')
      .select('name')
      .eq('firebase_uid', user.uid)
      .maybeSingle();

    return data?.name || user.displayName || 'A user';
  } catch {
    return user.displayName || 'A user';
  }
};

const sendVendorNotification = async (
  vendorId: string,
  userName: string,
  productName: string,
  shopName: string,
  action: 'ping' | 'cancelled' | 'received',
  reason?: string
) => {
  try {
    const vendorDetails = await getVendorDetails(vendorId);

    if (!vendorDetails.firebaseUid) {
      console.warn('⚠️ Could not find vendor Firebase UID for:', vendorId);
      return;
    }

    let notificationTitle = '';
    let notificationBody = '';

    switch (action) {
      case 'ping':
        notificationTitle = `⏰ Order Reminder ${shopName}`;
        notificationBody = `Remember my order! ${userName} ordered ${productName} at ${shopName}`;
        break;
      case 'cancelled':
        notificationTitle = `❌ Order Cancelled!! ${shopName}`;
        notificationBody = `${userName} cancelled ${productName} order.${reason ? ` Reason: ${reason}` : ''}`;
        break;
      case 'received':
        notificationTitle = `✅ Order Received! ${shopName}`;
        notificationBody = `${userName} received ${productName} ordered at ${shopName}. Funds are available for withdrawal!`;
        break;
    }

    const notificationData: any = {
      title: notificationTitle,
      body: notificationBody,
      notification_type: 'vendor',
      redirect_url: '/vendor/orders',
      data: {
        vendorId: vendorId,
        userId: auth.currentUser?.uid,
        userName: userName,
        productName: productName,
        shopName: shopName,
        action: action,
        reason: reason,
        type: 'order_update',
        timestamp: new Date().toISOString()
      }
    };

    notificationData.target_user_id = vendorDetails.firebaseUid;

    const SPARE_EMAIL = 'miracleglory2099@gmail.com';
    if (vendorDetails.businessEmail) {
      notificationData.email = vendorDetails.businessEmail;
    } else {
      notificationData.email = SPARE_EMAIL;
    }

    const response = await notificationService.sendNotification(notificationData);

    if (response && response.success) {
      const { error: insertError } = await supabase
        .from('user_notifications')
        .insert([{
          user_id: vendorDetails.firebaseUid,
          title: notificationTitle,
          message: notificationBody,
          type: 'order_update',
          data: notificationData.data,
          is_read: false,
          created_at: new Date().toISOString()
        }]);

      if (insertError) {
        console.error('❌ Error creating user_notification:', insertError);
      }
    }

  } catch (error) {
    console.error('❌ Error sending vendor notification:', error);
  }
};
// ========== END NOTIFICATION FUNCTIONS ==========

// ========== BUSINESS DEALS HELPERS ==========
type BusinessCategory = {
  id: string;
  name: string;
  table: string;
  icon: React.ReactNode;
};

const BUSINESS_CATEGORIES: BusinessCategory[] = [
  { id: 'food_wholesalers', name: 'Food Wholesalers', table: 'food_wholesalers', icon: <UtensilsCrossed size={16} /> },
  { id: 'banks', name: 'Banks', table: 'banks', icon: <Landmark size={16} /> },
  { id: 'airports', name: 'Airports', table: 'airports', icon: <Plane size={16} /> },
  { id: 'hotels', name: 'Hotels', table: 'hotels', icon: <Hotel size={16} /> },
  { id: 'construction_companies', name: 'Construction Companies', table: 'construction_companies', icon: <HardHat size={16} /> },
  { id: 'logistics_transport', name: 'Logistics & Transport', table: 'logistics_transport', icon: <TruckIcon size={16} /> },
  { id: 'big_dealers', name: 'Big Dealers', table: 'big_dealers', icon: <Briefcase size={16} /> },
  { id: 'import_export', name: 'Import & Export', table: 'import_export', icon: <Ship size={16} /> },
  { id: 'car_dealers', name: 'Car Dealers', table: 'car_dealers', icon: <Car size={16} /> },
  { id: 'real_estate', name: 'Real Estate', table: 'real_estate', icon: <Building2 size={16} /> },
  { id: 'insurance_companies', name: 'Insurance Companies', table: 'insurance_companies', icon: <Umbrella size={16} /> },
  { id: 'my_deals', name: 'My Deals', table: '', icon: <Handshake size={16} /> }
];

interface BusinessDeal {
  id: string;
  owner_id: string;
  owner_name?: string;
  business_name: string;
  description?: string;
  logo_url?: string;
  contact?: string;
  email?: string;
  phone?: string;
  website?: string;
  address?: string;
  location?: string;
  social_media?: string;
  category?: string;
  created_at?: string;
}

const PLACEHOLDER_DEALS: Record<string, BusinessDeal[]> = {
  food_wholesalers: [
    {
      id: 'ph-fw-1',
      owner_id: 'ph-owner-1',
      owner_name: 'Adebayo Ogunlesi',
      business_name: 'FreshFarm Wholesale Ltd',
      description: 'Bulk supplier of fresh produce, grains, and tubers to restaurants, hotels, and markets across Lagos and Ogun State. We deliver within 24 hours.',
      logo_url: '',
      contact: '0803 456 7890',
      email: 'orders@freshfarmwholesale.ng',
      phone: '0803 456 7890',
      website: 'www.freshfarmwholesale.ng',
      address: '12 Market Road, Mile 12, Lagos',
      location: 'Lagos, Nigeria',
      social_media: '@freshfarmwholesale',
      category: 'food_wholesalers',
      created_at: new Date(Date.now() - 86400000 * 3).toISOString()
    },
    {
      id: 'ph-fw-2',
      owner_id: 'ph-owner-2',
      owner_name: 'Chioma Nwosu',
      business_name: 'Golden Grain Distributors',
      description: 'Wholesale distribution of rice, beans, garri, and other staple foods. We supply in bags and cartons at competitive prices.',
      logo_url: '',
      contact: '0812 345 6789',
      email: 'chioma@goldengrain.ng',
      phone: '0812 345 6789',
      website: 'www.goldengrain.ng',
      address: '45 Aba Road, Port Harcourt',
      location: 'Rivers, Nigeria',
      social_media: '@goldengraindist',
      category: 'food_wholesalers',
      created_at: new Date(Date.now() - 86400000 * 7).toISOString()
    }
  ],
  banks: [
    {
      id: 'ph-bk-1',
      owner_id: 'ph-owner-3',
      owner_name: 'First National Bank',
      business_name: 'First National Bank Plc',
      description: 'Full-service commercial bank offering retail banking, corporate banking, investment services, and digital banking solutions across Nigeria.',
      logo_url: '',
      contact: '0700 123 4567',
      email: 'info@firstnationalbank.ng',
      phone: '0700 123 4567',
      website: 'www.firstnationalbank.ng',
      address: '1 Bank Plaza, Victoria Island, Lagos',
      location: 'Lagos, Nigeria',
      social_media: '@firstnationalbank',
      category: 'banks',
      created_at: new Date(Date.now() - 86400000 * 5).toISOString()
    }
  ],
  car_dealers: [
    {
      id: 'ph-cd-1',
      owner_id: 'ph-owner-4',
      owner_name: 'Emeka Okonkwo',
      business_name: 'AutoHub Nigeria',
      description: 'Certified pre-owned and brand new car dealership. We offer Toyota, Honda, Mercedes, and Lexus with financing options and warranty.',
      logo_url: '',
      contact: '0909 876 5432',
      email: 'sales@autohub.ng',
      phone: '0909 876 5432',
      website: 'www.autohub.ng',
      address: '78 Kudirat Abiola Way, Oregun, Lagos',
      location: 'Lagos, Nigeria',
      social_media: '@autohubnigeria',
      category: 'car_dealers',
      created_at: new Date(Date.now() - 86400000 * 2).toISOString()
    },
    {
      id: 'ph-cd-2',
      owner_id: 'ph-owner-5',
      owner_name: 'Fatima Bello',
      business_name: 'Northern Motors',
      description: 'Trusted car dealer in Northern Nigeria. Specializing in SUVs, pickups, and commercial vehicles. Trade-in accepted.',
      logo_url: '',
      contact: '0805 111 2233',
      email: 'fatima@northernmotors.ng',
      phone: '0805 111 2233',
      website: 'www.northernmotors.ng',
      address: '15 Ahmadu Bello Way, Kaduna',
      location: 'Kaduna, Nigeria',
      social_media: '@northernmotors',
      category: 'car_dealers',
      created_at: new Date(Date.now() - 86400000 * 10).toISOString()
    }
  ],
  real_estate: [
    {
      id: 'ph-re-1',
      owner_id: 'ph-owner-6',
      owner_name: 'Tunde Bakare',
      business_name: 'Prime Estates Ltd',
      description: 'Real estate development and property management. We sell lands, houses, and apartments in prime locations with verified titles.',
      logo_url: '',
      contact: '0814 555 6677',
      email: 'info@primeestates.ng',
      phone: '0814 555 6677',
      website: 'www.primeestates.ng',
      address: '3 Admiralty Way, Lekki Phase 1, Lagos',
      location: 'Lagos, Nigeria',
      social_media: '@primeestatesng',
      category: 'real_estate',
      created_at: new Date(Date.now() - 86400000 * 1).toISOString()
    }
  ]
};

const DEFAULT_CATEGORY = 'general';

const isValidTableName = (name: string): boolean => {
  return /^[a-z_][a-z0-9_]*$/.test(name);
};

const getCategoryTable = (categoryId: string): string => {
  const cat = BUSINESS_CATEGORIES.find(c => c.id === categoryId);
  return cat?.table || categoryId;
};

const getCategoryName = (categoryId: string): string => {
  const cat = BUSINESS_CATEGORIES.find(c => c.id === categoryId);
  return cat?.name || categoryId;
};

const fetchDeals = async (categoryId: string): Promise<{ deals: BusinessDeal[]; error: string | null }> => {
  try {
    const table = getCategoryTable(categoryId);
    if (!table || !isValidTableName(table)) {
      return { deals: [], error: null };
    }

    const { data, error } = await supabase
      .from(table)
      .select('*')
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) {
      if (error.code === '42P01') {
        return { deals: [], error: null };
      }
      if (error.code === 'PGRST301' || error.message?.includes('quota') || error.message?.includes('limit')) {
        return { deals: [], error: 'Service temporarily unavailable. Please check back later.' };
      }
      console.warn(`Fetch deals error for ${table}:`, error.message);
      return { deals: [], error: null };
    }

    return { deals: (data || []) as BusinessDeal[], error: null };
  } catch (err: any) {
    console.error('fetchDeals exception:', err);
    if (err?.message?.includes('network') || err?.message?.includes('fetch')) {
      return { deals: [], error: 'Network issue. Please check your connection and try again.' };
    }
    return { deals: [], error: null };
  }
};

const fetchMyDeals = async (userId: string): Promise<{ deals: BusinessDeal[]; error: string | null }> => {
  try {
    const allDeals: BusinessDeal[] = [];
    for (const cat of BUSINESS_CATEGORIES) {
      if (!cat.table || cat.id === 'my_deals') continue;
      try {
        const { data, error } = await supabase
          .from(cat.table)
          .select('*')
          .eq('owner_id', userId)
          .order('created_at', { ascending: false });
        if (error) {
          if (error.code === '42P01') continue;
          continue;
        }
        (data || []).forEach((d: any) => {
          allDeals.push({ ...d, category: cat.id });
        });
      } catch {
        continue;
      }
    }
    allDeals.sort((a, b) => {
      const da = a.created_at ? new Date(a.created_at).getTime() : 0;
      const db = b.created_at ? new Date(b.created_at).getTime() : 0;
      return db - da;
    });
    return { deals: allDeals, error: null };
  } catch (err: any) {
    console.error('fetchMyDeals exception:', err);
    return { deals: [], error: null };
  }
};

const launchDeal = async (
  categoryId: string,
  payload: Partial<BusinessDeal>
): Promise<{ success: boolean; error: string | null; data?: BusinessDeal }> => {
  try {
    const table = getCategoryTable(categoryId);
    if (!table || !isValidTableName(table)) {
      return { success: false, error: 'Invalid category.' };
    }

    const insertPayload: any = {
      owner_id: payload.owner_id,
      owner_name: payload.owner_name || null,
      business_name: payload.business_name || null,
      description: payload.description || null,
      logo_url: payload.logo_url || null,
      contact: payload.contact || null,
      email: payload.email || null,
      phone: payload.phone || null,
      website: payload.website || null,
      address: payload.address || null,
      location: payload.location || null,
      social_media: payload.social_media || null,
      category: categoryId,
      created_at: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from(table)
      .insert([insertPayload])
      .select()
      .single();

    if (error) {
      if (error.message?.includes('does not exist') || error.code === '42P01') {
        return { success: false, error: 'This category is not yet available. Please try another category.' };
      }
      if (error.code === 'PGRST301' || error.message?.includes('quota')) {
        return { success: false, error: 'Service temporarily unavailable. Please try again later.' };
      }
      console.error('launchDeal error:', error);
      return { success: false, error: 'Could not launch business. Please try again.' };
    }

    return { success: true, error: null, data: data as BusinessDeal };
  } catch (err: any) {
    console.error('launchDeal exception:', err);
    if (err?.message?.includes('network') || err?.message?.includes('fetch')) {
      return { success: false, error: 'Network issue. Please check your connection.' };
    }
    return { success: false, error: 'Something went wrong. Please try again.' };
  }
};
// ========== END BUSINESS DEALS HELPERS ==========

// Types
interface UserProfile {
  id: string;
  email: string;
  phone: string;
  name: string;
  role: string;
  isVerified: boolean;
  createdAt: string;
  profileImage?: string;
  bio?: string;
  location?: string;
  availableBalance?: number;
  pendingBalance?: number;
  currency?: string;
}

interface DashboardStat {
  id: string;
  label: string;
  value: number;
  icon: React.ReactNode;
  active: boolean;
}

interface Notification {
  id: string;
  type: 'info' | 'warning' | 'success' | 'pending';
  title: string;
  message: string;
  time: string;
  isRead: boolean;
  orderId?: string;
  vendorId?: string;
}

interface UtilityItem {
  id: string;
  name: string;
  icon: React.ReactNode;
  route: string;
}

interface Product {
  id: string;
  title: string;
  price: number;
  currency: string;
  images: string[];
  vendor_id: string;
  vendor_name: string;
  created_at: string;
  category?: string;
  condition?: string;
  inventory?: number;
  is_promoted?: boolean;
  views_count?: number;
  likes_count?: number;
  sales_count?: number;
}

interface VendorProfile {
  id: string;
  vendor_id: string;
  shop_name: string;
  profile_image: string;
  cover_image?: string;
  rating: number;
  followers_count: number;
  completed_trades: number;
  is_verified: boolean;
  online_status: boolean;
  contact_phone?: string;
  pending_balance?: number;
  available_balance?: number;
}

interface OrderItem {
  id: string;
  order_id: string;
  product_id: string;
  vendor_id: string;
  vendor_name: string;
  product_title: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  status: string;
  vendor_status: 'pending' | 'accepted' | 'rejected';
  user_status: 'pending' | 'received' | 'cancelled';
  rejection_reason?: string;
  created_at: string;
  updated_at: string;
  product_image?: string;
  vendor_phone?: string;
  response_time_minutes?: number;
}

interface OrderGroup {
  order_id: string;
  order_number: string;
  created_at: string;
  total_amount: number;
  items: OrderItem[];
  vendors: string[];
  delivery_location?: any;
}

interface Transaction {
  id: string;
  order_id: string;
  order_number: string;
  product_id: string;
  user_id: string;
  user_name: string;
  vendor_id: string;
  vendor_name: string;
  transaction_type: string;
  amount: number;
  description: string;
  user_status_at_time: string;
  vendor_status_at_time: string;
  rejection_reason?: string;
  order_created_at: string;
  transaction_created_at: string;
}

interface VendorOrderStats {
  total: number;
  pending: number;
  accepted: number;
  rejected: number;
  cancelled: number;
  received: number;
}

// ========== LOCATION NAME FETCHING HELPERS ==========
const fetchStateName = async (stateId: number): Promise<string> => {
  try {
    const { data, error } = await supabase
      .from('states')
      .select('name')
      .eq('id', stateId)
      .single();

    if (error || !data) return `State ID: ${stateId}`;
    return data.name;
  } catch {
    return `State ID: ${stateId}`;
  }
};

const fetchUniversityName = async (universityId: number): Promise<string> => {
  try {
    const { data, error } = await supabase
      .from('universities')
      .select('name')
      .eq('id', universityId)
      .single();

    if (error || !data) return `University ID: ${universityId}`;
    return data.name;
  } catch {
    return `University ID: ${universityId}`;
  }
};

const fetchCampusName = async (campusId: number): Promise<string> => {
  try {
    const { data, error } = await supabase
      .from('campuses')
      .select('name')
      .eq('id', campusId)
      .single();

    if (error || !data) return `Campus ID: ${campusId}`;
    return data.name;
  } catch {
    return `Campus ID: ${campusId}`;
  }
};

const formatLocationWithNames = async (deliveryLocation: any): Promise<any> => {
  if (!deliveryLocation) return null;

  const formattedLocation = { ...deliveryLocation };

  if (deliveryLocation.state_id && (!deliveryLocation.state_name || deliveryLocation.state_name.includes('ID:'))) {
    formattedLocation.state_name = await fetchStateName(deliveryLocation.state_id);
  }

  if (deliveryLocation.university_id && (!deliveryLocation.university_name || deliveryLocation.university_name.includes('ID:'))) {
    formattedLocation.university_name = await fetchUniversityName(deliveryLocation.university_id);
  }

  if (deliveryLocation.campus_id && (!deliveryLocation.campus_name || deliveryLocation.campus_name.includes('ID:'))) {
    formattedLocation.campus_name = await fetchCampusName(deliveryLocation.campus_id);
  }

  const locationParts = [
    formattedLocation.state_name,
    formattedLocation.university_name,
    formattedLocation.campus_name,
    deliveryLocation.precise_location
  ].filter(Boolean);

  formattedLocation.full_location = locationParts.join(', ');

  return formattedLocation;
};
// ========== END LOCATION NAME FETCHING HELPERS ==========

const UserDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [showBalance, setShowBalance] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [stats, setStats] = useState<DashboardStat[]>([
    { id: 'pending', label: 'Pending Orders', value: 0, icon: <Clock size={14} />, active: false },
    { id: 'processed', label: 'Processed Orders', value: 0, icon: <CheckCheck size={14} />, active: false },
    { id: 'received', label: 'Received Orders', value: 0, icon: <Truck size={14} />, active: false },
    { id: 'cart', label: 'In Cart', value: 0, icon: <ShoppingCart size={14} />, active: false },
    { id: 'favorites', label: 'Favorites', value: 0, icon: <Heart size={14} />, active: false },
    { id: 'rating', label: 'Rating', value: 0, icon: <Star size={14} />, active: false }
  ]);

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showAllNotifications, setShowAllNotifications] = useState(false);
  const [orderGroups, setOrderGroups] = useState<OrderGroup[]>([]);
  const [displayedOrders, setDisplayedOrders] = useState<OrderItem[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [recommendedProducts, setRecommendedProducts] = useState<Product[]>([]);
  const [vendorProfiles, setVendorProfiles] = useState<Record<string, VendorProfile>>({});
  const [activeTab, setActiveTab] = useState<string>('');
  const [processingAction, setProcessingAction] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState<{show: boolean, orderItem?: OrderItem, reason: string}>({show: false, reason: ''});
  const [showReceiveConfirm, setShowReceiveConfirm] = useState<{show: boolean, orderItem?: OrderItem}>({show: false});
  const [showReview, setShowReview] = useState<{show: boolean, orderItem?: OrderItem}>({show: false});
  const [reviewData, setReviewData] = useState({rating: 5, review: ''});
  const [featureUnavailable, setFeatureUnavailable] = useState<string | null>(null);
  const [expandedOrderGroups, setExpandedOrderGroups] = useState<Set<string>>(new Set());
  const [orderStats, setOrderStats] = useState({
    new: 0,
    total: 0,
    accepted: 0,
    received: 0,
    cancelled: 0
  });

  const [showOrderInfoModal, setShowOrderInfoModal] = useState<{show: boolean, orderItem?: OrderItem, orderGroup?: OrderGroup, vendorPhone?: string, formattedLocation?: any}>({show: false});

  const [vendorOrderStats, setVendorOrderStats] = useState<VendorOrderStats>({
    total: 0,
    pending: 0,
    accepted: 0,
    rejected: 0,
    cancelled: 0,
    received: 0
  });

  // ========== BUSINESS DEALS STATE ==========
  const [showBusinessModal, setShowBusinessModal] = useState<{ show: boolean; categoryId: string }>({ show: false, categoryId: '' });
  const [dealsList, setDealsList] = useState<BusinessDeal[]>([]);
  const [dealsLoading, setDealsLoading] = useState(false);
  const [dealsError, setDealsError] = useState<string | null>(null);
  const [showLaunchModal, setShowLaunchModal] = useState(false);
  const [launchCategoryId, setLaunchCategoryId] = useState<string>('');
  const [launchForm, setLaunchForm] = useState({
    business_name: '',
    description: '',
    logo_url: '',
    contact: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    location: '',
    social_media: ''
  });
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [showDealView, setShowDealView] = useState<{ show: boolean; deal?: BusinessDeal; categoryId?: string }>({ show: false });
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  // ========== END BUSINESS DEALS STATE ==========

  const reviewPresets = [
    {rating: 5, text: 'Excellent product! Highly recommended'},
    {rating: 4, text: 'Very good quality, satisfied with purchase'},
    {rating: 3, text: 'Good product, meets expectations'},
    {rating: 2, text: 'Below average, needs improvement'},
    {rating: 1, text: 'Poor quality, would not recommend'}
  ];

  const cancelReasons = [
    'No longer interested',
    'Vendor response too slow',
    'Found better price elsewhere',
    'Changed my mind',
    'Product not needed anymore',
    'Financial constraints'
  ];

  // ========== UPDATED UTILITIES ==========
  const utilities: UtilityItem[] = useMemo(() =>
    BUSINESS_CATEGORIES.map(cat => ({
      id: cat.id,
      name: cat.name,
      icon: cat.icon,
      route: '#business'
    }))
  , []);
  // ========== END UPDATED UTILITIES ==========

  const calculateOrderGroupProgress = (items: OrderItem[]): number => {
    if (items.length === 0) return 0;

    const processedItems = items.filter(item =>
      item.vendor_status === 'accepted' ||
      item.vendor_status === 'rejected' ||
      item.user_status === 'cancelled' ||
      item.user_status === 'received'
    ).length;

    return Math.round((processedItems / items.length) * 100);
  };

  const fetchVendorPhone = async (vendorId: string): Promise<string> => {
    try {
      const { data: vendorProfile, error } = await supabase
        .from('vendor_profiles')
        .select('contact_phone')
        .eq('vendor_id', vendorId)
        .maybeSingle();

      if (error) {
        return 'Not available';
      }

      return vendorProfile?.contact_phone || 'Not available';
    } catch {
      return 'Not available';
    }
  };

  const handleViewOrderInfo = async (orderItem: OrderItem, orderGroup: OrderGroup) => {
    const vendorPhone = await fetchVendorPhone(orderItem.vendor_id);

    let formattedLocation = null;
    if (orderGroup.delivery_location) {
      formattedLocation = await formatLocationWithNames(orderGroup.delivery_location);
    }

    setShowOrderInfoModal({show: true, orderItem, orderGroup, vendorPhone, formattedLocation});
  };

  const fetchVendorPendingOrders = async (userId: string) => {
    try {
      const { data: vendorProfiles, error: vendorError } = await supabase
        .from('vendor_profiles')
        .select('vendor_id')
        .eq('user_id', userId);

      if (vendorError || !vendorProfiles || vendorProfiles.length === 0) {
        setVendorOrderStats({
          total: 0, pending: 0, accepted: 0, rejected: 0, cancelled: 0, received: 0
        });
        return;
      }

      const vendorIds = vendorProfiles.map(v => v.vendor_id);

      const [
        { count: totalCount },
        { count: pendingCount },
        { count: acceptedCount },
        { count: rejectedCount },
        { count: cancelledCount },
        { count: receivedCount }
      ] = await Promise.all([
        supabase.from('order_items').select('*', { count: 'exact', head: true }).in('vendor_id', vendorIds),
        supabase.from('order_items').select('*', { count: 'exact', head: true }).in('vendor_id', vendorIds).eq('vendor_status', 'pending').neq('user_status', 'cancelled'),
        supabase.from('order_items').select('*', { count: 'exact', head: true }).in('vendor_id', vendorIds).eq('vendor_status', 'accepted'),
        supabase.from('order_items').select('*', { count: 'exact', head: true }).in('vendor_id', vendorIds).eq('vendor_status', 'rejected'),
        supabase.from('order_items').select('*', { count: 'exact', head: true }).in('vendor_id', vendorIds).eq('user_status', 'cancelled'),
        supabase.from('order_items').select('*', { count: 'exact', head: true }).in('vendor_id', vendorIds).eq('user_status', 'received')
      ]);

      setVendorOrderStats({
        total: totalCount || 0,
        pending: pendingCount || 0,
        accepted: acceptedCount || 0,
        rejected: rejectedCount || 0,
        cancelled: cancelledCount || 0,
        received: receivedCount || 0
      });
    } catch {
      setVendorOrderStats({
        total: 0, pending: 0, accepted: 0, rejected: 0, cancelled: 0, received: 0
      });
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        await fetchUserData(user.uid);
        await fetchUserStats(user.uid);
        await fetchNotifications(user.uid);
        await fetchOrderGroups(user.uid);
        await fetchTransactions(user.uid);
        await fetchTopProducts();
        await fetchVendorPendingOrders(user.uid);
      } else {
        navigate('/signin');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  const fetchUserData = async (userId: string) => {
    try {
      const { data: userData, error } = await supabase
        .from('users')
        .select('*')
        .eq('firebase_uid', userId)
        .single();

      if (error) throw error;

      const userAvailableBalance = Number(userData.balance) || 0;
      const userPendingBalance = Number(userData.pending_balance) || 0;

      const { data: vendorShops } = await supabase
        .from('vendor_profiles')
        .select('available_balance, pending_balance')
        .eq('user_id', userId);

      let totalVendorAvailable = 0;
      let totalVendorPending = 0;

      if (vendorShops && vendorShops.length > 0) {
        totalVendorAvailable = vendorShops.reduce((sum, shop) => sum + (Number(shop.available_balance) || 0), 0);
        totalVendorPending = vendorShops.reduce((sum, shop) => sum + (Number(shop.pending_balance) || 0), 0);
      }

      const combinedAvailableBalance = userAvailableBalance + totalVendorAvailable;
      const combinedPendingBalance = userPendingBalance + totalVendorPending;

      const profile: UserProfile = {
        id: userData.id,
        email: userData.email || '',
        phone: userData.phone || '',
        name: userData.name || 'User',
        role: userData.user_type || 'user',
        isVerified: userData.is_active || false,
        createdAt: userData.created_at || new Date().toISOString(),
        profileImage: userData.avatar_url || '',
        availableBalance: combinedAvailableBalance,
        pendingBalance: combinedPendingBalance,
        currency: 'NGN'
      };

      setUserProfile(profile);
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const fetchUserStats = async (userId: string) => {
    try {
      const { data: orders } = await supabase
        .from('orders')
        .select('id')
        .eq('user_id', userId);

      let pendingCount = 0;
      let processedCount = 0;
      let receivedCount = 0;
      let ratingAvg = 0;

      if (orders && orders.length > 0) {
        const orderIds = orders.map(order => order.id);
        const { data: orderItems } = await supabase
          .from('order_items')
          .select('*')
          .in('order_id', orderIds);

        if (orderItems) {
          pendingCount = orderItems.filter(item =>
            item.vendor_status === 'pending' && item.user_status !== 'cancelled'
          ).length;

          processedCount = orderItems.filter(item =>
            item.vendor_status === 'accepted' || item.vendor_status === 'rejected'
          ).length;

          receivedCount = orderItems.filter(item => item.user_status === 'received').length;
        }

        const { data: reviews } = await supabase
          .from('reviews')
          .select('rating')
          .eq('user_id', userId);

        if (reviews && reviews.length > 0) {
          const totalRating = reviews.reduce((sum, review) => sum + review.rating, 0);
          ratingAvg = Math.round((totalRating / reviews.length) * 10) / 10;
        }
      }

      const { count: cartCount } = await supabase
        .from('carts')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      const { count: favoritesCount } = await supabase
        .from('user_favorites')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      setStats([
        { id: 'pending', label: 'Pending Orders', value: pendingCount, icon: <Clock size={14} />, active: false },
        { id: 'processed', label: 'Processed Orders', value: processedCount, icon: <CheckCheck size={14} />, active: false },
        { id: 'received', label: 'Received Orders', value: receivedCount, icon: <Truck size={14} />, active: false },
        { id: 'cart', label: 'In Cart', value: cartCount || 0, icon: <ShoppingCart size={14} />, active: false },
        { id: 'favorites', label: 'Favorites', value: favoritesCount || 0, icon: <Heart size={14} />, active: false },
        { id: 'rating', label: 'Rating', value: ratingAvg, icon: <Star size={14} />, active: false }
      ]);
    } catch (error) {
      console.error('Error fetching user stats:', error);
    }
  };

  const fetchNotifications = async (userId: string) => {
    try {
      const { data: orders } = await supabase
        .from('orders')
        .select('id, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      const notificationsList: Notification[] = [];

      if (!orders || orders.length === 0) {
        setNotifications([{
          id: 'notif-welcome',
          type: 'info',
          title: 'Welcome!',
          message: 'No orders yet. Start shopping!',
          time: 'Just now',
          isRead: false
        }]);
        return;
      }

      const orderIds = orders.map(order => order.id);
      const { data: orderItems } = await supabase
        .from('order_items')
        .select('*')
        .in('order_id', orderIds);

      if (!orderItems) {
        setNotifications([{
          id: 'notif-welcome',
          type: 'info',
          title: 'Welcome!',
          message: 'No orders yet. Start shopping!',
          time: 'Just now',
          isRead: false
        }]);
        return;
      }

      const newOrders = orderItems.filter(item =>
        item.vendor_status === 'pending' && item.user_status !== 'cancelled'
      ).length;

      if (newOrders > 0) {
        notificationsList.push({
          id: 'notif-new',
          type: 'pending',
          title: 'New Orders',
          message: `${newOrders} new order${newOrders > 1 ? 's' : ''} waiting for vendor`,
          time: 'Just now',
          isRead: false
        });
      }

      const acceptedCount = orderItems.filter(item => item.vendor_status === 'accepted').length;
      if (acceptedCount > 0) {
        notificationsList.push({
          id: 'notif-accepted',
          type: 'success',
          title: 'Accepted Orders',
          message: `${acceptedCount} order${acceptedCount > 1 ? 's' : ''} accepted by vendors`,
          time: 'Recently',
          isRead: false
        });
      }

      const rejectedCount = orderItems.filter(item => item.vendor_status === 'rejected').length;
      if (rejectedCount > 0) {
        notificationsList.push({
          id: 'notif-rejected',
          type: 'warning',
          title: 'Rejected Orders',
          message: `${rejectedCount} order${rejectedCount > 1 ? 's' : ''} rejected by vendors`,
          time: 'Recently',
          isRead: false
        });
      }

      const cancelledCount = orderItems.filter(item => item.user_status === 'cancelled').length;
      if (cancelledCount > 0) {
        notificationsList.push({
          id: 'notif-cancelled',
          type: 'info',
          title: 'Cancelled Orders',
          message: `${cancelledCount} order${cancelledCount > 1 ? 's' : ''} cancelled`,
          time: 'Recently',
          isRead: false
        });
      }

      const receivedCount = orderItems.filter(item => item.user_status === 'received').length;
      if (receivedCount > 0) {
        notificationsList.push({
          id: 'notif-received',
          type: 'success',
          title: 'Received Orders',
          message: `${receivedCount} order${receivedCount > 1 ? 's' : ''} received successfully`,
          time: 'Recently',
          isRead: false
        });
      }

      if (notificationsList.length === 0) {
        notificationsList.push({
          id: 'notif-welcome',
          type: 'info',
          title: 'Welcome!',
          message: 'No orders yet. Start shopping!',
          time: 'Just now',
          isRead: false
        });
      }

      setNotifications(notificationsList);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const fetchOrderGroups = async (userId: string) => {
    try {
      const { data: orders, error: ordersError } = await supabase
        .from('orders')
        .select('*, delivery_location')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (ordersError) throw ordersError;

      if (!orders || orders.length === 0) {
        setOrderGroups([]);
        setDisplayedOrders([]);
        setOrderStats({ new: 0, total: 0, accepted: 0, received: 0, cancelled: 0 });
        return;
      }

      const orderIds = orders.map(order => order.id);
      const { data: orderItems, error: itemsError } = await supabase
        .from('order_items')
        .select('*')
        .in('order_id', orderIds)
        .order('created_at', { ascending: false });

      if (itemsError) throw itemsError;

      const groups: OrderGroup[] = [];
      const allOrderItems: OrderItem[] = [];
      let totalNew = 0;
      let totalAccepted = 0;
      let totalReceived = 0;
      let totalCancelled = 0;

      for (const order of orders) {
        const items = orderItems?.filter(item => item.order_id === order.id) || [];

        if (items.length > 0) {
          const itemsWithDetails = await Promise.all(
            items.map(async (item) => {
              const { data: product } = await supabase
                .from('products')
                .select('images')
                .eq('id', item.product_id)
                .single();

              const { data: vendorProfile } = await supabase
                .from('vendor_profiles')
                .select('*')
                .eq('vendor_id', item.vendor_id)
                .single();

              if (item.vendor_status === 'pending' && item.user_status !== 'cancelled') totalNew++;
              if (item.vendor_status === 'accepted') totalAccepted++;
              if (item.user_status === 'received') totalReceived++;
              if (item.user_status === 'cancelled') totalCancelled++;

              return {
                ...item,
                product_image: product?.images?.[0] || '',
                vendor_phone: vendorProfile?.contact_phone || '',
                vendor_name: vendorProfile?.shop_name || item.vendor_name
              };
            })
          );

          allOrderItems.push(...itemsWithDetails);

          const vendorIds = Array.from(new Set(items.map(item => item.vendor_id)));

          groups.push({
            order_id: order.id,
            order_number: order.order_number || `ORD${order.id.slice(0, 8).toUpperCase()}`,
            created_at: order.created_at,
            total_amount: Number(order.total_amount) || 0,
            items: itemsWithDetails,
            vendors: vendorIds,
            delivery_location: order.delivery_location
          });
        }
      }

      setOrderGroups(groups);
      setDisplayedOrders(allOrderItems);
      setOrderStats({
        new: totalNew,
        total: allOrderItems.length,
        accepted: totalAccepted,
        received: totalReceived,
        cancelled: totalCancelled
      });
    } catch (error) {
      console.error('Error fetching order groups:', error);
      setOrderGroups([]);
      setDisplayedOrders([]);
      setOrderStats({ new: 0, total: 0, accepted: 0, received: 0, cancelled: 0 });
    }
  };

  const fetchTransactions = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', userId)
        .order('transaction_created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      setTransactions(data || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]);
    }
  };

  const fetchTopProducts = async () => {
    try {
      const { data: productsData, error } = await supabase
        .from('products')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(6);

      if (error) throw error;

      const products: Product[] = productsData || [];
      setRecommendedProducts(products);

      const vendorIds = products.map(p => p.vendor_id).filter(Boolean);
      const { data: vendorsData } = await supabase
        .from('vendor_profiles')
        .select('*')
        .in('vendor_id', vendorIds);

      const profiles: Record<string, VendorProfile> = {};
      vendorsData?.forEach(vendor => {
        profiles[vendor.vendor_id] = {
          id: vendor.id,
          vendor_id: vendor.vendor_id,
          shop_name: vendor.shop_name || 'Shop',
          profile_image: vendor.profile_image || '',
          cover_image: vendor.cover_image,
          rating: vendor.rating || 0,
          followers_count: vendor.followers_count || 0,
          completed_trades: vendor.completed_trades || 0,
          is_verified: vendor.is_verified || false,
          online_status: vendor.online_status || false,
          contact_phone: vendor.contact_phone || '',
          pending_balance: vendor.pending_balance || 0,
          available_balance: vendor.available_balance || 0
        };
      });

      setVendorProfiles(profiles);
    } catch (error) {
      console.error('Error fetching top products:', error);
      setRecommendedProducts([]);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'NGN'): string => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hr ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleNotificationClick = () => {
    setActiveTab('orders');
    setTimeout(() => {
      const ordersSection = document.getElementById('orders-section');
      if (ordersSection) {
        window.scrollTo({
          top: ordersSection.offsetTop - 30,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  // ========== UPDATED UTILITY CLICK HANDLER ==========
  const handleUtilityClick = (utility: UtilityItem) => {
    const isBusiness = BUSINESS_CATEGORIES.some(c => c.id === utility.id);
    if (isBusiness) {
      openBusinessModal(utility.id);
    } else if (utility.id === 'orders') {
      setActiveTab('orders');
      setTimeout(() => {
        const ordersSection = document.getElementById('orders-section');
        if (ordersSection) {
          window.scrollTo({
            top: ordersSection.offsetTop - 30,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else if (utility.id === 'transactions') {
      setActiveTab('transactions');
      setTimeout(() => {
        const transactionsSection = document.getElementById('transactions-section');
        if (transactionsSection) {
          window.scrollTo({
            top: transactionsSection.offsetTop - 30,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else if (utility.id === 'shop') {
      navigate('/vendor/dashboard');
    } else if (utility.id === 'cart') {
      navigate('/cart');
    } else if (utility.id === 'favorites') {
      navigate('/favorites');
    } else {
      setFeatureUnavailable(utility.name);
      setTimeout(() => setFeatureUnavailable(null), 3000);
    }
  };
  // ========== END UPDATED UTILITY CLICK HANDLER ==========

  // ========== BUSINESS DEALS FUNCTIONS ==========
  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  /**
   * Build the list of deals for the "My Deals" modal.
   * - Pulls all deals the user has launched across every category.
   * - If the user has no deals yet, injects a default "My Account" deal
   *   so the Boost button is always available to promote their account.
   */
  const buildMyDealsList = (fetchedDeals: BusinessDeal[]): BusinessDeal[] => {
    const user = auth.currentUser;
    const defaultDeal: BusinessDeal = {
      id: `default-account-${user?.uid || 'me'}`,
      owner_id: user?.uid || 'me',
      owner_name: userProfile?.name || 'My Account',
      business_name: 'My Account',
      description:
        'Boost your account to get more visibility on GoStorez. Promote your profile, shop, and listings to reach more buyers.',
      logo_url: userProfile?.profileImage || '',
      contact: userProfile?.name || '',
      email: userProfile?.email || '',
      phone: userProfile?.phone || '',
      website: '',
      address: '',
      location: userProfile?.location || '',
      social_media: '',
      category: 'my_deals',
      created_at: new Date().toISOString()
    };

    if (!fetchedDeals || fetchedDeals.length === 0) {
      return [defaultDeal];
    }

    // Ensure "My Account" is always first
    return [defaultDeal, ...fetchedDeals];
  };

  const openBusinessModal = async (categoryId: string) => {
    setShowBusinessModal({ show: true, categoryId });
    setDealsLoading(true);
    setDealsError(null);
    setDealsList([]);

    const user = auth.currentUser;
    if (!user) {
      setDealsLoading(false);
      return;
    }

    if (categoryId === 'my_deals') {
      const { deals, error } = await fetchMyDeals(user.uid);
      if (error) {
        setDealsError(error);
      } else {
        setDealsList(buildMyDealsList(deals));
      }
      setDealsLoading(false);
      return;
    }

    const { deals, error } = await fetchDeals(categoryId);
    if (error) {
      setDealsError(error);
    } else {
      if (deals.length === 0) {
        const placeholders = PLACEHOLDER_DEALS[categoryId];
        if (placeholders && placeholders.length > 0) {
          setDealsList(placeholders);
        } else {
          setDealsList([]);
        }
      } else {
        setDealsList(deals);
      }
    }
    setDealsLoading(false);
  };

  const closeBusinessModal = () => {
    setShowBusinessModal({ show: false, categoryId: '' });
    setDealsList([]);
    setDealsError(null);
  };

  const openLaunchModal = (categoryId: string) => {
    setLaunchCategoryId(categoryId);
    setLaunchForm({
      business_name: '',
      description: '',
      logo_url: '',
      contact: '',
      email: '',
      phone: '',
      website: '',
      address: '',
      location: '',
      social_media: ''
    });
    setLaunchError(null);
    setShowLaunchModal(true);
  };

  const closeLaunchModal = () => {
    setShowLaunchModal(false);
    setLaunchError(null);
  };

  const handleLaunchSubmit = async () => {
    const user = auth.currentUser;
    if (!user) {
      setLaunchError('You must be signed in to launch a business.');
      return;
    }

    setLaunching(true);
    setLaunchError(null);

    const payload: Partial<BusinessDeal> = {
      owner_id: user.uid,
      owner_name: userProfile?.name || 'User',
      business_name: launchForm.business_name || null as any,
      description: launchForm.description || null as any,
      logo_url: launchForm.logo_url || null as any,
      contact: launchForm.contact || null as any,
      email: launchForm.email || null as any,
      phone: launchForm.phone || null as any,
      website: launchForm.website || null as any,
      address: launchForm.address || null as any,
      location: launchForm.location || null as any,
      social_media: launchForm.social_media || null as any,
      category: launchCategoryId
    };

    const { success, error, data } = await launchDeal(launchCategoryId, payload);

    if (!success) {
      setLaunchError(error || 'Could not launch business.');
      setLaunching(false);
      return;
    }

    setLaunching(false);
    closeLaunchModal();
    showToast('Business launched successfully!');

    if (showBusinessModal.show) {
      await openBusinessModal(showBusinessModal.categoryId);
    }
  };

  const openDealView = (deal: BusinessDeal, categoryId: string) => {
    setShowDealView({ show: true, deal, categoryId });
  };

  const closeDealView = () => {
    setShowDealView({ show: false });
  };
  // ========== END BUSINESS DEALS FUNCTIONS ==========

  const handleStatClick = (statId: string) => {
    const updatedStats = stats.map(stat => ({
      ...stat,
      active: stat.id === statId ? !stat.active : false
    }));

    setStats(updatedStats);

    if (statId === 'pending') {
      const filtered = getAllOrderItems().filter(item =>
        item.vendor_status === 'pending' && item.user_status !== 'cancelled'
      );
      setDisplayedOrders(updatedStats.find(s => s.id === statId)?.active ? filtered : getAllOrderItems());
    } else if (statId === 'processed') {
      const filtered = getAllOrderItems().filter(item =>
        item.vendor_status === 'accepted' || item.vendor_status === 'rejected'
      );
      setDisplayedOrders(updatedStats.find(s => s.id === statId)?.active ? filtered : getAllOrderItems());
    } else if (statId === 'received') {
      const filtered = getAllOrderItems().filter(item => item.user_status === 'received');
      setDisplayedOrders(updatedStats.find(s => s.id === statId)?.active ? filtered : getAllOrderItems());
    } else {
      setDisplayedOrders(getAllOrderItems());
    }
  };

  const getAllOrderItems = () => {
    return orderGroups.flatMap(group => group.items);
  };

  const handlePingVendor = async (orderItem: OrderItem) => {
    setProcessingAction(`ping-${orderItem.id}`);
    try {
      const userName = userProfile?.name || await getCurrentUserName();

      setTimeout(() => {
        setProcessingAction(null);
        const successDiv = document.createElement('div');
        successDiv.className = 'order-action-success';
        successDiv.textContent = 'Vendor notified successfully';
        successDiv.style.cssText = `
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: rgba(0, 0, 0, 0.8);
          color: white;
          padding: 10px 20px;
          border-radius: 5px;
          z-index: 10000;
        `;
        document.body.appendChild(successDiv);
        setTimeout(() => document.body.removeChild(successDiv), 2000);
      }, 500);

      sendVendorNotification(
        orderItem.vendor_id,
        userName,
        orderItem.product_title,
        orderItem.vendor_name,
        'ping'
      );

    } catch (error) {
      console.error('Error pinging vendor:', error);
      setProcessingAction(null);
    }
  };

  const handleChatVendor = async (orderItem: OrderItem) => {
    setProcessingAction(`chat-${orderItem.id}`);
    try {
      await navigator.clipboard.writeText(orderItem.vendor_id);

      const successDiv = document.createElement('div');
      successDiv.className = 'order-action-success';
      successDiv.textContent = 'Secured chat ID copied! Redirecting...';
      successDiv.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: rgba(0, 0, 0, 0.8);
        color: white;
        padding: 10px 20px;
        border-radius: 5px;
        z-index: 10000;
      `;
      document.body.appendChild(successDiv);

      setTimeout(() => {
        document.body.removeChild(successDiv);
        navigate('/chats');
      }, 1500);
    } catch (error) {
      console.error('Error copying chat ID:', error);
    } finally {
      setProcessingAction(null);
    }
  };

  const handleCallVendor = (orderItem: OrderItem) => {
    if (orderItem.vendor_phone) {
      window.location.href = `tel:${orderItem.vendor_phone}`;
    } else {
      const errorDiv = document.createElement('div');
      errorDiv.className = 'order-action-error';
      errorDiv.textContent = 'Vendor phone not available';
      errorDiv.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: rgba(255, 0, 0, 0.8);
        color: white;
        padding: 10px 20px;
        border-radius: 5px;
        z-index: 10000;
      `;
      document.body.appendChild(errorDiv);
      setTimeout(() => document.body.removeChild(errorDiv), 2000);
    }
  };

  const handleCancelOrder = async (orderItem: OrderItem, reason: string) => {
    setProcessingAction(`cancel-${orderItem.id}`);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('User not authenticated');

      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, balance')
        .eq('firebase_uid', currentUser.uid)
        .single();

      if (userError) throw userError;
      if (!userData) throw new Error('User data not found');

      const { error: updateError } = await supabase
        .from('order_items')
        .update({
          user_status: 'cancelled',
          updated_at: new Date().toISOString()
        })
        .eq('id', orderItem.id);

      if (updateError) throw updateError;

      const { data: vendorData } = await supabase
        .from('vendor_profiles')
        .select('pending_balance, vendor_id')
        .eq('vendor_id', orderItem.vendor_id)
        .single();

      if (vendorData) {
        const currentVendorPendingBalance = Number(vendorData.pending_balance) || 0;
        const newVendorPendingBalance = Math.max(0, currentVendorPendingBalance - orderItem.total_price);

        await supabase
          .from('vendor_profiles')
          .update({
            pending_balance: newVendorPendingBalance,
            updated_at: new Date().toISOString()
          })
          .eq('vendor_id', orderItem.vendor_id);
      }

      const currentUserBalance = Number(userData.balance) || 0;
      const newUserBalance = currentUserBalance + orderItem.total_price;

      await supabase
        .from('users')
        .update({
          balance: newUserBalance,
          updated_at: new Date().toISOString()
        })
        .eq('firebase_uid', currentUser.uid);

      setUserProfile(prev => prev ? {
        ...prev,
        availableBalance: newUserBalance
      } : null);

      try {
        await supabase
          .from('transactions')
          .insert({
            order_id: orderItem.order_id,
            order_number: orderGroups.find(g => g.order_id === orderItem.order_id)?.order_number || orderItem.order_id,
            product_id: orderItem.product_id,
            user_id: currentUser.uid,
            user_name: userProfile?.name || 'User',
            vendor_id: orderItem.vendor_id,
            vendor_name: orderItem.vendor_name,
            transaction_type: 'order_cancelled',
            amount: orderItem.total_price,
            description: `Order cancelled by user: ${reason}`,
            user_status_at_time: 'cancelled',
            vendor_status_at_time: orderItem.vendor_status,
            rejection_reason: reason,
            order_created_at: orderItem.created_at,
            transaction_created_at: new Date().toISOString()
          });
      } catch {
        // non-critical
      }

      const userName = userProfile?.name || await getCurrentUserName();
      sendVendorNotification(
        orderItem.vendor_id,
        userName,
        orderItem.product_title,
        orderItem.vendor_name,
        'cancelled',
        reason
      );

      await fetchUserData(currentUser.uid);
      await fetchUserStats(currentUser.uid);
      await fetchNotifications(currentUser.uid);
      await fetchOrderGroups(currentUser.uid);
      await fetchTransactions(currentUser.uid);

      setShowCancelConfirm({show: false, reason: ''});
      setProcessingAction(null);

      const successDiv = document.createElement('div');
      successDiv.textContent = 'Order cancelled successfully! Funds returned to your account.';
      successDiv.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: rgba(0, 128, 0, 0.9);
        color: white;
        padding: 12px 24px;
        border-radius: 5px;
        z-index: 10000;
        text-align: center;
        font-weight: 600;
      `;
      document.body.appendChild(successDiv);
      setTimeout(() => document.body.removeChild(successDiv), 3000);

    } catch (error) {
      console.error('Error cancelling order:', error);
      setProcessingAction(null);

      const errorDiv = document.createElement('div');
      errorDiv.textContent = 'Error cancelling order. Please try again.';
      errorDiv.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: rgba(255, 0, 0, 0.9);
        color: white;
        padding: 12px 24px;
        border-radius: 5px;
        z-index: 10000;
        text-align: center;
        font-weight: 600;
      `;
      document.body.appendChild(errorDiv);
      setTimeout(() => document.body.removeChild(errorDiv), 3000);
    }
  };

  const handleReceiveOrder = async (orderItem: OrderItem) => {
    setProcessingAction(`receive-${orderItem.id}`);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('User not authenticated');

      const { error: updateError } = await supabase
        .from('order_items')
        .update({
          user_status: 'received',
          updated_at: new Date().toISOString()
        })
        .eq('id', orderItem.id);

      if (updateError) throw updateError;

      const { data: vendorData, error: vendorError } = await supabase
        .from('vendor_profiles')
        .select('pending_balance, available_balance, vendor_id')
        .eq('vendor_id', orderItem.vendor_id)
        .single();

      if (vendorError) throw vendorError;
      if (!vendorData) throw new Error('Vendor data not found');

      const currentVendorPendingBalance = Number(vendorData.pending_balance) || 0;
      const currentVendorAvailableBalance = Number(vendorData.available_balance) || 0;

      const newVendorPendingBalance = Math.max(0, currentVendorPendingBalance - orderItem.total_price);
      const newVendorAvailableBalance = currentVendorAvailableBalance + orderItem.total_price;

      const { error: vendorUpdateError } = await supabase
        .from('vendor_profiles')
        .update({
          pending_balance: newVendorPendingBalance,
          available_balance: newVendorAvailableBalance,
          updated_at: new Date().toISOString()
        })
        .eq('vendor_id', orderItem.vendor_id);

      if (vendorUpdateError) throw vendorUpdateError;

      try {
        await supabase
          .from('transactions')
          .insert({
            order_id: orderItem.order_id,
            order_number: orderGroups.find(g => g.order_id === orderItem.order_id)?.order_number || orderItem.order_id,
            product_id: orderItem.product_id,
            user_id: currentUser.uid,
            user_name: userProfile?.name || 'User',
            vendor_id: orderItem.vendor_id,
            vendor_name: orderItem.vendor_name,
            transaction_type: 'order_received',
            amount: orderItem.total_price,
            description: 'Order marked as received by buyer',
            user_status_at_time: 'received',
            vendor_status_at_time: orderItem.vendor_status,
            order_created_at: orderItem.created_at,
            transaction_created_at: new Date().toISOString()
          });
      } catch {
        // non-critical
      }

      const userName = userProfile?.name || await getCurrentUserName();
      sendVendorNotification(
        orderItem.vendor_id,
        userName,
        orderItem.product_title,
        orderItem.vendor_name,
        'received'
      );

      await fetchUserData(currentUser.uid);
      await fetchUserStats(currentUser.uid);
      await fetchNotifications(currentUser.uid);
      await fetchOrderGroups(currentUser.uid);
      await fetchTransactions(currentUser.uid);

      setShowReceiveConfirm({show: false});
      setShowReview({show: true, orderItem});
      setProcessingAction(null);

      const successDiv = document.createElement('div');
      successDiv.textContent = 'Order received successfully! Funds transferred to vendor.';
      successDiv.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: rgba(0, 128, 0, 0.9);
        color: white;
        padding: 12px 24px;
        border-radius: 5px;
        z-index: 10000;
        text-align: center;
        font-weight: 600;
      `;
      document.body.appendChild(successDiv);
      setTimeout(() => document.body.removeChild(successDiv), 3000);

    } catch (error) {
      console.error('Error receiving order:', error);
      setProcessingAction(null);

      const errorDiv = document.createElement('div');
      errorDiv.textContent = 'Error receiving order. Please try again.';
      errorDiv.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: rgba(255, 0, 0, 0.9);
        color: white;
        padding: 12px 24px;
        border-radius: 5px;
        z-index: 10000;
        text-align: center;
        font-weight: 600;
      `;
      document.body.appendChild(errorDiv);
      setTimeout(() => document.body.removeChild(errorDiv), 3000);
    }
  };

  const handleSubmitReview = async () => {
    if (!showReview.orderItem || !reviewData.review.trim()) return;

    setProcessingAction(`review-${showReview.orderItem.id}`);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('User not authenticated');

      const { error: reviewError } = await supabase
        .from('reviews')
        .insert({
          product_id: showReview.orderItem.product_id,
          vendor_id: showReview.orderItem.vendor_id,
          user_id: currentUser.uid,
          user_name: userProfile?.name || 'User',
          rating: reviewData.rating,
          review_text: reviewData.review,
          order_item_id: showReview.orderItem.id,
          created_at: new Date().toISOString()
        });

      if (reviewError) throw reviewError;

      await fetchUserData(currentUser.uid);
      await fetchUserStats(currentUser.uid);

      setShowReview({show: false});
      setReviewData({rating: 5, review: ''});
      setProcessingAction(null);

      const successDiv = document.createElement('div');
      successDiv.textContent = 'Review submitted successfully!';
      successDiv.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: rgba(0, 128, 0, 0.9);
        color: white;
        padding: 12px 24px;
        border-radius: 5px;
        z-index: 10000;
        text-align: center;
        font-weight: 600;
      `;
      document.body.appendChild(successDiv);
      setTimeout(() => document.body.removeChild(successDiv), 2000);

    } catch (error) {
      console.error('Error submitting review:', error);
      setProcessingAction(null);
    }
  };

  const handleProductClick = (vendorId: string, productId: string) => {
    navigate(`/vendor/${vendorId}`, {
      state: {
        initialProductId: productId,
        fromDashboard: true
      }
    });
  };

  const handleVendorClick = (vendorId: string) => {
    navigate(`/vendor/${vendorId}`);
  };

  const toggleOrderGroup = (orderId: string) => {
    const newExpanded = new Set(expandedOrderGroups);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrderGroups(newExpanded);
  };

  const cartCount = stats.find(s => s.id === 'cart')?.value || 0;
  const favoritesCount = stats.find(s => s.id === 'favorites')?.value || 0;
  const pendingOrdersCount = stats.find(s => s.id === 'pending')?.value || 0;

  if (loading && !userProfile) {
    return (
      <div className="userdashboard-loading">
        <div className="userdashboard-loading-spinner">
          <RefreshCw className="userdashboard-animate-spin" size={32} />
        </div>
      </div>
    );
  }

  return (
    <div className="userdashboard-container">
      {/* Fixed Header */}
      <header className="userdashboard-header">
        <div className="userdashboard-header-left">
          {userProfile?.profileImage ? (
            <img
              src={getThumbnailUrl(userProfile.profileImage, 100, 70)}
              alt="User"
              className="userdashboard-avatar"
              referrerPolicy="no-referrer"
              onError={(e) => {
                const img = e.currentTarget;
                if (img.dataset.fallback === 'true') return;
                img.dataset.fallback = 'true';
                img.src = getOriginalUrl(userProfile.profileImage || '');
              }}
              onClick={() => navigate('/user/profile')}
            />
          ) : (
            <div
              className="userdashboard-avatar-placeholder"
              onClick={() => navigate('/user/profile')}
            >
              {userProfile?.name?.charAt(0) || 'U'}
            </div>
          )}
          <div className="userdashboard-welcome">
            <span className="userdashboard-welcome-text">Welcome back,</span>
            <span className="userdashboard-user-name">{userProfile ? userProfile.name.split(' ')[0] : 'User Login'}</span>
          </div>
        </div>

        <div className="userdashboard-header-right">
          {/* === My Ads button next to notification icon === */}
          <MyAdsButton variant="icon-label" label="My Ads" />

          <button
            className="userdashboard-notification-btn" onClick={() => navigate('/notifications')}
          >
            <Bell size={18} />
            {notifications.length > 0 && (
              <span className="userdashboard-notification-badge">{notifications.length}</span>
            )}
          </button>

          <button
            className="userdashboard-refresh-btn"
            onClick={async () => {
              setLoading(true);
              if (auth.currentUser) {
                await fetchUserData(auth.currentUser.uid);
                await fetchUserStats(auth.currentUser.uid);
                await fetchNotifications(auth.currentUser.uid);
                await fetchOrderGroups(auth.currentUser.uid);
                await fetchTransactions(auth.currentUser.uid);
                await fetchTopProducts();
                await fetchVendorPendingOrders(auth.currentUser.uid);
              }
              setLoading(false);
            }}
            disabled={loading}
          >
            <RefreshCw size={18} className={loading ? 'userdashboard-animate-spin' : ''} />
          </button>
        </div>
      </header>

      {/* Balance Section */}
      <section className="userdashboard-balance-section">
        <div className="userdashboard-balance-container">
          <div className="userdashboard-balance-left">
            <div className="userdashboard-balance-label">Available Balance</div>
            <div className="userdashboard-balance-amount">
              {showBalance ? formatCurrency(userProfile?.availableBalance || 0, userProfile?.currency) : '••••••'}
            </div>

            <div className="userdashboard-balance-actions">
              <button
                className="userdashboard-fund-btn"
                onClick={() => navigate('/money')}
              >
                <Wallet size={14} />
                <span style={{ marginLeft: '4px', fontSize: '10px' }}>Deposit</span>
              </button>
              <button
                className="userdashboard-transfer-btn"
                onClick={() => navigate('/money')}
              >
                <Send size={14} />
                <span style={{ marginLeft: '4px', fontSize: '10px' }}>Withdraw</span>
              </button>
              <button
                className="userdashboard-eye-btn"
                onClick={() => setShowBalance(!showBalance)}
              >
                {showBalance ? <Eye size={14} /> : <EyeOff size={14} />}
              </button>
            </div>
          </div>

          <div className="userdashboard-balance-right">
            <div className="userdashboard-balance-label">Pending Balance</div>
            <div className="userdashboard-balance-amount">
              {showBalance ? formatCurrency(userProfile?.pendingBalance || 0, userProfile?.currency) : '••••••'}
            </div>
            <div className="userdashboard-balance-subtext">Unprocessed funds</div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="userdashboard-stats-section">
        <div className="userdashboard-stats-grid">
          {stats.map(stat => (
            <div
              key={stat.id}
              className={`userdashboard-stat-card ${stat.active ? 'active' : ''}`}
              onClick={() => handleStatClick(stat.id)}
            >
              <div className="userdashboard-stat-icon">{stat.icon}</div>
              <div className="userdashboard-stat-content">
                <div className="userdashboard-stat-value">{stat.value}</div>
                <div className="userdashboard-stat-label">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Notifications */}
      <section className="userdashboard-notifications-section">
        <div className="userdashboard-notifications-header" onClick={handleNotificationClick}>
          <h3 className="userdashboard-section-title">Notifications</h3>
          <ChevronRight size={14} />
          {notifications.length > 0 && (
            <span className="userdashboard-notification-count">{notifications.length}</span>
          )}
        </div>

        <div className="userdashboard-notifications-list">
          {(showAllNotifications ? notifications : notifications.slice(0, 3)).map(notification => (
            <div
              key={notification.id}
              className={`userdashboard-notification-item ${notification.isRead ? '' : 'unread'}`}
              onClick={handleNotificationClick}
            >
              <div className="userdashboard-notification-icon">
                {notification.type === 'success' && <CheckCircle size={12} />}
                {notification.type === 'warning' && <AlertTriangle size={12} />}
                {notification.type === 'info' && <Info size={12} />}
                {notification.type === 'pending' && <Clock size={12} />}
              </div>
              <div className="userdashboard-notification-content">
                <div className="userdashboard-notification-title">{notification.title}</div>
                <div className="userdashboard-notification-message">{notification.message}</div>
              </div>
              <div className="userdashboard-notification-time">{notification.time}</div>
            </div>
          ))}
          {notifications.length > 3 && !showAllNotifications && (
            <div
              className="userdashboard-notification-more"
              onClick={() => setShowAllNotifications(true)}
            >
              Show {notifications.length - 3} more notifications
            </div>
          )}
          {showAllNotifications && (
            <div
              className="userdashboard-notification-less"
              onClick={() => setShowAllNotifications(false)}
            >
              Show less
            </div>
          )}
        </div>
      </section>

      {/* Utilities */}
      <section className="userdashboard-utilities-section">
        <div className="userdashboard-utilities-header">
          <h3 className="userdashboard-section-title">Business Deals • Verified Booking & Reservation </h3>
          <ChevronRight size={14} />
        </div>

        <div className="userdashboard-utilities-grid">
          {utilities.map(utility => (
            <button
              key={utility.id}
              className="userdashboard-utility-card"
              onClick={() => handleUtilityClick(utility)}
            >
              <div className="userdashboard-utility-icon">{utility.icon}</div>
              <span className="userdashboard-utility-name">{utility.name}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Orders Section */}
      {activeTab === 'orders' && (
        <section id="orders-section" className="userdashboard-orders-section">
          <div className="userdashboard-orders-header">
            <h3 className="userdashboard-section-title">My Orders</h3>
            <span className="userdashboard-orders-count">{displayedOrders.length} item{displayedOrders.length !== 1 ? 's' : ''}</span>
          </div>

          <div className="userdashboard-order-stats-overview">
            <div className="userdashboard-order-stat-overview-card">
              <div className="userdashboard-order-stat-overview-icon"><Package size={12} /></div>
              <div className="userdashboard-order-stat-overview-content">
                <div className="userdashboard-order-stat-overview-value">{orderStats.new}</div>
                <div className="userdashboard-order-stat-overview-label">New Orders</div>
              </div>
            </div>
            <div className="userdashboard-order-stat-overview-card">
              <div className="userdashboard-order-stat-overview-icon"><Package size={12} /></div>
              <div className="userdashboard-order-stat-overview-content">
                <div className="userdashboard-order-stat-overview-value">{orderStats.total}</div>
                <div className="userdashboard-order-stat-overview-label">Total Orders</div>
              </div>
            </div>
            <div className="userdashboard-order-stat-overview-card">
              <div className="userdashboard-order-stat-overview-icon"><CheckCircle size={12} /></div>
              <div className="userdashboard-order-stat-overview-content">
                <div className="userdashboard-order-stat-overview-value">{orderStats.accepted}</div>
                <div className="userdashboard-order-stat-overview-label">Accepted</div>
              </div>
            </div>
            <div className="userdashboard-order-stat-overview-card">
              <div className="userdashboard-order-stat-overview-icon"><Truck size={12} /></div>
              <div className="userdashboard-order-stat-overview-content">
                <div className="userdashboard-order-stat-overview-value">{orderStats.received}</div>
                <div className="userdashboard-order-stat-overview-label">Received</div>
              </div>
            </div>
            <div className="userdashboard-order-stat-overview-card">
              <div className="userdashboard-order-stat-overview-icon"><XCircle size={12} /></div>
              <div className="userdashboard-order-stat-overview-content">
                <div className="userdashboard-order-stat-overview-value">{orderStats.cancelled}</div>
                <div className="userdashboard-order-stat-overview-label">Cancelled</div>
              </div>
            </div>
          </div>

          <div className="userdashboard-order-groups">
            {orderGroups.length === 0 ? (
              <div className="userdashboard-empty-state">
                <div className="userdashboard-empty-icon">
                  <Package size={32} />
                </div>
                <h4>Searching for orders...</h4>
                <p>When you make purchases, your orders will appear here</p>
              </div>
            ) : (
              orderGroups.map(group => {
                const progressPercentage = calculateOrderGroupProgress(group.items);

                return (
                  <div key={group.order_id} className="userdashboard-order-group">
                    <div
                      className="userdashboard-order-group-header"
                      onClick={() => toggleOrderGroup(group.order_id)}
                    >
                      <div className="userdashboard-order-group-info">
                        <h4>Order #{group.order_number}</h4>
                        <span className="userdashboard-order-date">{formatDate(group.created_at)}</span>
                        <span className="userdashboard-order-amount">{formatCurrency(group.total_amount)}</span>
                        <span className="userdashboard-order-vendors">{group.vendors.length} vendor{group.vendors.length !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="userdashboard-order-group-progress">
                        <div className="userdashboard-progress-bar">
                          <div
                            className="userdashboard-progress-fill"
                            style={{ width: `${progressPercentage}%` }}
                          ></div>
                        </div>
                        <div className="userdashboard-progress-text">{progressPercentage}%</div>
                      </div>
                      <div className="userdashboard-order-group-toggle">
                        {expandedOrderGroups.has(group.order_id) ? (
                          <ChevronRight size={12} className="rotated" />
                        ) : (
                          <ChevronRight size={12} />
                        )}
                      </div>
                    </div>

                    {expandedOrderGroups.has(group.order_id) && (
                      <div className="userdashboard-order-group-items">
                        {group.items.map(item => {
                          const itemThumbSrc = item.product_image
                            ? getThumbnailUrl(item.product_image, 200, 65)
                            : '';
                          const itemOriginalSrc = item.product_image || '';

                          return (
                            <div key={item.id} className="userdashboard-order-item">
                              <div className="userdashboard-order-item-header">
                                <div className="userdashboard-order-item-image">
                                  {itemThumbSrc ? (
                                    <img
                                      src={itemThumbSrc}
                                      alt={item.product_title}
                                      loading="lazy"
                                      referrerPolicy="no-referrer"
                                      onError={(e) => {
                                        const img = e.currentTarget;
                                        if (img.dataset.fallback === 'true') return;
                                        img.dataset.fallback = 'true';
                                        img.src = getOriginalUrl(itemOriginalSrc);
                                      }}
                                    />
                                  ) : (
                                    <div className="userdashboard-order-item-noimage">
                                      <Package size={24} />
                                    </div>
                                  )}
                                </div>
                                <div className="userdashboard-order-item-info">
                                  <h5>{item.product_title}</h5>
                                  <div className="userdashboard-order-item-details">
                                    <span>ID: {item.product_id.slice(0, 8)}</span>
                                    <span>{item.quantity} × {formatCurrency(item.unit_price)}</span>
                                  </div>
                                  <div className="userdashboard-order-item-status">
                                    <span className={`userdashboard-order-status-badge status-${item.vendor_status}`}>
                                      Vendor: {item.vendor_status}
                                    </span>
                                    <span className={`userdashboard-order-status-badge status-${item.user_status}`}>
                                      You: {item.user_status}
                                    </span>
                                    <button
                                      className="userdashboard-order-info-btn"
                                      onClick={() => handleViewOrderInfo(item, group)}
                                      title="View full order details"
                                    >
                                      <Info size={12} />
                                    </button>
                                  </div>
                                </div>
                                <div className="userdashboard-order-item-price">
                                  {formatCurrency(item.total_price)}
                                </div>
                              </div>

                              <div className="userdashboard-order-vendor-info">
                                <div className="userdashboard-order-vendor-details">
                                  <div className="userdashboard-order-vendor-name">
                                    <strong>Vendor:</strong> {item.vendor_name}
                                  </div>
                                  <div className="userdashboard-order-vendor-actions">
                                    <button
                                      className="userdashboard-vendor-action-btn"
                                      onClick={() => handleProductClick(item.vendor_id, item.product_id)}
                                    >
                                      <ShoppingBag size={10} />
                                      View Product
                                    </button>
                                    <button
                                      className="userdashboard-vendor-action-btn"
                                      onClick={() => handleVendorClick(item.vendor_id)}
                                    >
                                      <Store size={10} />
                                      Visit Shop
                                    </button>
                                  </div>
                                </div>
                              </div>

                              <div className="userdashboard-order-item-actions">
                                {item.user_status === 'cancelled' ? (
                                  <div className="userdashboard-order-cancelled">
                                    Order was cancelled by you
                                  </div>
                                ) : item.user_status === 'received' ? (
                                  <div className="userdashboard-order-received">
                                    Order received successfully
                                  </div>
                                ) : item.vendor_status === 'rejected' ? (
                                  <div className="userdashboard-order-rejected">
                                    {item.rejection_reason
                                      ? `Order rejected by vendor: ${item.rejection_reason}`
                                      : 'Order rejected by vendor'
                                    }
                                  </div>
                                ) : (
                                  <>
                                    <button
                                      className="userdashboard-order-action-btn ping"
                                      onClick={() => handlePingVendor(item)}
                                      disabled={processingAction === `ping-${item.id}`}
                                    >
                                      {processingAction === `ping-${item.id}` ? (
                                        <Loader size={12} className="userdashboard-spinning" />
                                      ) : (
                                        <Bell size={12} />
                                      )}
                                      <span>Ping Vendor</span>
                                    </button>

                                    <button
                                      className="userdashboard-order-action-btn chat"
                                      onClick={() => handleChatVendor(item)}
                                      disabled={processingAction === `chat-${item.id}`}
                                    >
                                      {processingAction === `chat-${item.id}` ? (
                                        <Loader size={12} className="userdashboard-spinning" />
                                      ) : (
                                        <MessageCircle size={12} />
                                      )}
                                      <span>Chat</span>
                                    </button>

                                    <button
                                      className="userdashboard-order-action-btn call"
                                      onClick={() => handleCallVendor(item)}
                                      disabled={!item.vendor_phone}
                                    >
                                      <PhoneCall size={12} />
                                      <span>Call</span>
                                    </button>

                                    {item.vendor_status === 'pending' && (
                                      <button
                                        className="userdashboard-order-action-btn cancel"
                                        onClick={() => setShowCancelConfirm({show: true, orderItem: item, reason: ''})}
                                        disabled={processingAction?.startsWith('cancel')}
                                      >
                                        <XCircle size={12} />
                                        <span>Cancel Order</span>
                                      </button>
                                    )}

                                    {item.vendor_status === 'accepted' && (
                                      <button
                                        className="userdashboard-order-action-btn receive"
                                        onClick={() => setShowReceiveConfirm({show: true, orderItem: item})}
                                        disabled={processingAction?.startsWith('receive')}
                                      >
                                        <CheckCircle size={12} />
                                        <span>Receive Order</span>
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>

                              {item.rejection_reason && (
                                <div className="userdashboard-order-rejection">
                                  <strong>Rejection Reason:</strong> {item.rejection_reason}
                                </div>
                              )}

                              {item.response_time_minutes && (
                                <div className="userdashboard-order-response-time">
                                  <strong>Response Time:</strong> {item.response_time_minutes} minutes
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </section>
      )}

      {/* Transactions Section */}
      {activeTab === 'transactions' && (
        <section id="transactions-section" className="userdashboard-transactions-section">
          <div className="userdashboard-transactions-header">
            <h3 className="userdashboard-section-title">Transaction History</h3>
          </div>

          <div className="userdashboard-transactions-list">
            {transactions.length === 0 ? (
              <div className="userdashboard-empty-state">
                <div className="userdashboard-empty-icon">
                  <CreditCard size={32} />
                </div>
                <h4>No Transactions Yet</h4>
                <p>Your transaction history will appear here</p>
              </div>
            ) : (
              transactions.map(transaction => (
                <div key={transaction.id} className="userdashboard-transaction-item">
                  <div className="userdashboard-transaction-header">
                    <div className="userdashboard-transaction-type">
                      <span className={`userdashboard-transaction-type-badge type-${transaction.transaction_type}`}>
                        {transaction.transaction_type.replace('_', ' ')}
                      </span>
                      <span className="userdashboard-transaction-order">
                        Order: {transaction.order_number?.slice(0, 8) || 'N/A'}
                      </span>
                    </div>
                    <div className="userdashboard-transaction-amount">
                      {formatCurrency(transaction.amount)}
                    </div>
                  </div>

                  <div className="userdashboard-transaction-details">
                    <div className="userdashboard-transaction-detail">
                      <span>Vendor:</span>
                      <span>{transaction.vendor_name}</span>
                    </div>
                    <div className="userdashboard-transaction-detail">
                      <span>Description:</span>
                      <span>{transaction.description}</span>
                    </div>
                    <div className="userdashboard-transaction-detail">
                      <span>Date:</span>
                      <span>{formatDate(transaction.transaction_created_at)}</span>
                    </div>
                    {transaction.rejection_reason && (
                      <div className="userdashboard-transaction-detail">
                        <span>Rejection Reason:</span>
                        <span>{transaction.rejection_reason}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      )}

      {/* Top Products Section */}
      <section className="userdashboard-topproducts-section">
        <div className="userdashboard-topproducts-header">
          <h3 className="userdashboard-section-title">Newest Products</h3>
          <button
            className="userdashboard-view-all-btn"
            onClick={() => navigate('/market')}
          >
            See More
          </button>
        </div>

        {recommendedProducts.length === 0 ? (
          <div className="userdashboard-empty-products">
            <ShoppingBag size={48} className="userdashboard-empty-products-icon" />
            <h3 className="userdashboard-empty-products-title">No products yet</h3>
            <p className="userdashboard-empty-products-subtitle">
              Check back later for new products
            </p>
          </div>
        ) : (
          <div className="userdashboard-topproducts-list">
            {recommendedProducts.map((product) => {
              const vendor = vendorProfiles[product.vendor_id];

              const originalImg =
                product.images && product.images[0] ? product.images[0] : '';
              const thumbSrc = originalImg
                ? getThumbnailUrl(originalImg, 400, 65)
                : '';

              const originalVendorLogo = vendor?.profile_image || '';
              const vendorLogoSrc = originalVendorLogo
                ? getThumbnailUrl(originalVendorLogo, 80, 70)
                : '';

              return (
                <div
                  key={product.id}
                  className="userdashboard-topproduct-item"
                  onClick={() => handleProductClick(product.vendor_id, product.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="userdashboard-topproduct-image">
                    {thumbSrc ? (
                      <img
                        src={thumbSrc}
                        alt={product.title}
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        onError={(e) => {
                          const img = e.currentTarget;
                          if (img.dataset.fallback === 'true') return;
                          img.dataset.fallback = 'true';
                          if (originalImg) {
                            img.src = getOriginalUrl(originalImg);
                          } else {
                            img.style.display = 'none';
                          }
                        }}
                      />
                    ) : (
                      <div className="userdashboard-topproduct-noimage">
                        <ShoppingBag size={24} />
                      </div>
                    )}

                    {product.is_promoted && (
                      <div className="userdashboard-topproduct-promotedbadge">
                        Promoted
                      </div>
                    )}
                  </div>

                  <div className="userdashboard-topproduct-details">
                    <div className="userdashboard-topproduct-top">
                      <h3 className="userdashboard-topproduct-title">
                        {product.title || 'Product'}
                      </h3>
                      <div className="userdashboard-topproduct-price">
                        {formatCurrency(product.price || 0)}
                      </div>
                    </div>

                    <div className="userdashboard-topproduct-bottom">
                      <div className="userdashboard-topproduct-info">
                        {product.category && (
                          <span className="userdashboard-topproduct-category">
                            {product.category}
                          </span>
                        )}
                        {product.condition && (
                          <span className="userdashboard-topproduct-condition">
                            {product.condition}
                          </span>
                        )}
                      </div>

                      <div className="userdashboard-topproduct-stats">
                        <div className="userdashboard-topproduct-stat">
                          <Eye size={10} />
                          <span>{product.views_count || 0}</span>
                        </div>
                        <div className="userdashboard-topproduct-stat">
                          <Heart size={10} />
                          <span>{product.likes_count || 0}</span>
                        </div>
                        <div className="userdashboard-topproduct-stat">
                          <Package size={10} />
                          <span>{product.inventory || 0}</span>
                        </div>
                      </div>
                    </div>

                    {vendor && (
                      <div
                        className="userdashboard-topproduct-shopinfo"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVendorClick(product.vendor_id);
                        }}
                      >
                        {vendorLogoSrc ? (
                          <img
                            src={vendorLogoSrc}
                            alt={vendor.shop_name}
                            className="userdashboard-topproduct-shoplogo"
                            loading="lazy"
                            referrerPolicy="no-referrer"
                            onError={(e) => {
                              const img = e.currentTarget;
                              if (img.dataset.fallback === 'true') return;
                              img.dataset.fallback = 'true';
                              if (originalVendorLogo) {
                                img.src = getOriginalUrl(originalVendorLogo);
                              } else {
                                img.style.display = 'none';
                              }
                            }}
                          />
                        ) : (
                          <div className="userdashboard-topproduct-shoplogo userdashboard-topproduct-shoplogo-placeholder">
                            {vendor.shop_name?.charAt(0) || 'S'}
                          </div>
                        )}
                        <span className="userdashboard-topproduct-shopname">{vendor.shop_name || 'Shop'}</span>
                        {vendor.is_verified && <span className="userdashboard-topproduct-shopverified">✓</span>}
                      </div>
                    )}

                    <div
                      className="userdashboard-topproduct-actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        className="userdashboard-topproduct-actionbtn cart"
                        onClick={() => handleProductClick(product.vendor_id, product.id)}
                      >
                        <ShoppingBag size={12} />
                        View Product
                      </button>

                      {product.vendor_id && (
                        <button
                          className="userdashboard-topproduct-actionbtn view"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVendorClick(product.vendor_id);
                          }}
                        >
                          <Store size={12} />
                          Visit Shop
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ORDER INFO MODAL */}
      {showOrderInfoModal.show && showOrderInfoModal.orderItem && (
        <div className="userdashboard-info-modal-overlay" onClick={() => setShowOrderInfoModal({show: false})}>
          <div className="userdashboard-info-modal" onClick={(e) => e.stopPropagation()}>
            <div className="userdashboard-info-header">
              <h3>
                <Info size={12} />
                Order Details
              </h3>
              <button className="userdashboard-info-close" onClick={() => setShowOrderInfoModal({show: false})}>
                <X size={14} />
              </button>
            </div>
            <div className="userdashboard-info-body">
              <div className="userdashboard-info-section">
                <div className="userdashboard-info-section-title">
                  <Package size={10} />
                  Product
                </div>
                <div className="userdashboard-info-row">
                  <span>Product ID:</span>
                  <span className="code">{showOrderInfoModal.orderItem.product_id}</span>
                </div>
                <div className="userdashboard-info-row">
                  <span>Title:</span>
                  <span><strong>{showOrderInfoModal.orderItem.product_title}</strong></span>
                </div>
                <div className="userdashboard-info-row">
                  <span>Quantity:</span>
                  <span>{showOrderInfoModal.orderItem.quantity}</span>
                </div>
                <div className="userdashboard-info-row">
                  <span>Unit Price:</span>
                  <span>{formatCurrency(showOrderInfoModal.orderItem.unit_price)}</span>
                </div>
                <div className="userdashboard-info-row">
                  <span>Total Price:</span>
                  <span><strong>{formatCurrency(showOrderInfoModal.orderItem.total_price)}</strong></span>
                </div>
              </div>

              <div className="userdashboard-info-section">
                <div className="userdashboard-info-section-title">
                  <Store size={10} />
                  Vendor
                </div>
                <div className="userdashboard-info-row">
                  <span>Vendor ID:</span>
                  <span className="code">{showOrderInfoModal.orderItem.vendor_id}</span>
                </div>
                <div className="userdashboard-info-row">
                  <span>Vendor Name:</span>
                  <span>{showOrderInfoModal.orderItem.vendor_name}</span>
                </div>
                <div className="userdashboard-info-row">
                  <span>Vendor Phone:</span>
                  <span className="contact-phone">{showOrderInfoModal.vendorPhone || 'Loading...'}</span>
                </div>
                <div className="userdashboard-info-row">
                  <span>Vendor Status:</span>
                  <span className={`status-${showOrderInfoModal.orderItem.vendor_status}`}>
                    {showOrderInfoModal.orderItem.vendor_status}
                  </span>
                </div>
                <div className="userdashboard-info-row">
                  <span>User Status:</span>
                  <span className={`status-${showOrderInfoModal.orderItem.user_status}`}>
                    {showOrderInfoModal.orderItem.user_status}
                  </span>
                </div>
                {showOrderInfoModal.orderItem.rejection_reason && (
                  <div className="userdashboard-info-row">
                    <span>Rejection Reason:</span>
                    <span style={{color: '#dc3545'}}>{showOrderInfoModal.orderItem.rejection_reason}</span>
                  </div>
                )}
              </div>

              <div className="userdashboard-info-section">
                <div className="userdashboard-info-section-title">
                  <Clock size={10} />
                  Timeline
                </div>
                <div className="userdashboard-info-row">
                  <span>Order Created:</span>
                  <span className="userdashboard-info-timestamp">
                    {new Date(showOrderInfoModal.orderItem.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="userdashboard-info-row">
                  <span>Last Updated:</span>
                  <span className="userdashboard-info-timestamp">
                    {new Date(showOrderInfoModal.orderItem.updated_at).toLocaleString()}
                  </span>
                </div>
                {showOrderInfoModal.orderItem.response_time_minutes && (
                  <div className="userdashboard-info-row">
                    <span>Response Time:</span>
                    <span>{showOrderInfoModal.orderItem.response_time_minutes} minutes</span>
                  </div>
                )}
              </div>

              <div className="userdashboard-info-section">
                <div className="userdashboard-info-section-title">
                  <MapPin size={10} />
                  Delivery Location
                </div>
                <div className="userdashboard-info-location">
                  {showOrderInfoModal.formattedLocation ? (
                    <>
                      {showOrderInfoModal.formattedLocation.full_location && (
                        <><p><strong>📍 Full Address:</strong></p><p>{showOrderInfoModal.formattedLocation.full_location}</p></>
                      )}
                      {showOrderInfoModal.formattedLocation.state_name && !showOrderInfoModal.formattedLocation.state_name.includes('ID:') && (
                        <p><strong>State:</strong> {showOrderInfoModal.formattedLocation.state_name}</p>
                      )}
                      {showOrderInfoModal.formattedLocation.university_name && !showOrderInfoModal.formattedLocation.university_name.includes('ID:') && (
                        <p><strong>University:</strong> {showOrderInfoModal.formattedLocation.university_name}</p>
                      )}
                      {showOrderInfoModal.formattedLocation.campus_name && !showOrderInfoModal.formattedLocation.campus_name.includes('ID:') && (
                        <p><strong>Campus:</strong> {showOrderInfoModal.formattedLocation.campus_name}</p>
                      )}
                      {showOrderInfoModal.formattedLocation.precise_location && (
                        <p><strong>Precise Location:</strong> {showOrderInfoModal.formattedLocation.precise_location}</p>
                      )}
                    </>
                  ) : (
                    <p>No delivery location information available</p>
                  )}
                </div>
              </div>

              <div className="userdashboard-info-section">
                <div className="userdashboard-info-section-title">
                  <ShoppingCart size={10} />
                  Order Reference
                </div>
                <div className="userdashboard-info-row">
                  <span>Order Item ID:</span>
                  <span className="code">{showOrderInfoModal.orderItem.id}</span>
                </div>
                <div className="userdashboard-info-row">
                  <span>Order ID:</span>
                  <span className="code">{showOrderInfoModal.orderItem.order_id}</span>
                </div>
                {showOrderInfoModal.orderGroup && (
                  <div className="userdashboard-info-row">
                    <span>Order Number:</span>
                    <span className="code">{showOrderInfoModal.orderGroup.order_number}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========== BUSINESS DEALS FULL PAGE MODAL ========== */}
      {showBusinessModal.show && (
        <div className="userdashboard-business-modal-overlay">
          <div className="userdashboard-business-modal">
            <div className="userdashboard-business-modal-header">
              <div className="userdashboard-business-modal-title-row">
                <h2>{getCategoryName(showBusinessModal.categoryId)}</h2>
                <span className="userdashboard-business-count">
                  {dealsList.length} deal{dealsList.length !== 1 ? 's' : ''}
                </span>
              </div>
              <button className="userdashboard-business-modal-close" onClick={closeBusinessModal}>
                <X size={18} />
              </button>
            </div>

            <div className="userdashboard-business-modal-body">
              {/* Launch Business Button */}
              {showBusinessModal.categoryId !== 'my_deals' && (
                <button
                  className="userdashboard-launch-business-btn"
                  onClick={() => openLaunchModal(showBusinessModal.categoryId)}
                >
                  <Plus size={14} />
                  <span>Launch Your Business</span>
                </button>
              )}

              {/* Deals List */}
              {dealsLoading ? (
                <div className="userdashboard-business-loading">
                  <Loader size={24} className="userdashboard-spinning" />
                  <p>Loading deals...</p>
                </div>
              ) : dealsError ? (
                <div className="userdashboard-business-error">
                  <Wifi size={32} />
                  <p>{dealsError}</p>
                  <button
                    className="userdashboard-business-retry-btn"
                    onClick={() => openBusinessModal(showBusinessModal.categoryId)}
                  >
                    <RefreshCw size={12} />
                    Retry
                  </button>
                </div>
              ) : dealsList.length === 0 ? (
                <div className="userdashboard-business-empty">
                  <Briefcase size={40} />
                  <h3>No deals yet</h3>
                  <p>Be the first to launch a business in this category!</p>
                </div>
              ) : (
                <div className="userdashboard-business-list">
                  {dealsList.map((deal) => {
                    const isMyDeals = showBusinessModal.categoryId === 'my_deals';
                    return (
                      <div
                        key={deal.id}
                        className="userdashboard-business-card"
                        onClick={() => openDealView(deal, showBusinessModal.categoryId)}
                      >
                        <div className="userdashboard-business-card-logo">
                          {deal.logo_url ? (
                            <img
                              src={getThumbnailUrl(deal.logo_url, 120, 70)}
                              alt={deal.business_name || 'Business'}
                              loading="lazy"
                              referrerPolicy="no-referrer"
                              onError={(e) => {
                                const img = e.currentTarget;
                                if (img.dataset.fallback === 'true') return;
                                img.dataset.fallback = 'true';
                                img.src = getOriginalUrl(deal.logo_url || '');
                              }}
                            />
                          ) : (
                            <div className="userdashboard-business-card-logo-placeholder">
                              {(deal.business_name || 'B').charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="userdashboard-business-card-info">
                          <h4>{deal.business_name || 'Unnamed Business'}</h4>
                          <p className="userdashboard-business-card-desc">
                            {deal.description || 'No description provided'}
                          </p>
                          <div className="userdashboard-business-card-meta">
                            {deal.location && (
                              <span><MapPin size={9} /> {deal.location}</span>
                            )}
                            {deal.owner_name && (
                              <span><User size={9} /> {deal.owner_name}</span>
                            )}
                          </div>
                        </div>

                        {/* === Boost button on every user‑created deal (My Deals only) === */}
                        {isMyDeals && (
                          <div
                            className="userdashboard-business-card-boost"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <AdsPromoteButton
                              target={{
                                type: 'business_deal',
                                id: deal.id,
                                name: deal.business_name || 'My Business',
                                image: deal.logo_url || undefined,
                                category: deal.category || showBusinessModal.categoryId,
                                extra: {
                                  owner_id: deal.owner_id,
                                  owner_name: deal.owner_name,
                                  phone: deal.phone,
                                  email: deal.email,
                                  website: deal.website,
                                  location: deal.location,
                                  address: deal.address
                                }
                              }}
                              label="Boost"
                              variant="icon-label"
                              className="userdashboard-deal-boost-btn"
                            />
                          </div>
                        )}

                        <ChevronRight size={14} className="userdashboard-business-card-arrow" />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ========== LAUNCH BUSINESS MODAL ========== */}
      {showLaunchModal && (
        <div className="userdashboard-launch-modal-overlay">
          <div className="userdashboard-launch-modal">
            <div className="userdashboard-launch-modal-header">
              <h2>
                <Store size={16} />
                Launch Business
              </h2>
              <button onClick={closeLaunchModal}>
                <X size={16} />
              </button>
            </div>
            <div className="userdashboard-launch-modal-body">
              {launchError && (
                <div className="userdashboard-launch-error">
                  <AlertTriangle size={12} />
                  {launchError}
                </div>
              )}

              <div className="userdashboard-launch-field">
                <label>Business Name</label>
                <input
                  type="text"
                  placeholder="Enter business name"
                  value={launchForm.business_name}
                  onChange={(e) => setLaunchForm({ ...launchForm, business_name: e.target.value })}
                />
              </div>

              <div className="userdashboard-launch-field">
                <label>Description</label>
                <textarea
                  placeholder="Tell us about your business"
                  rows={3}
                  value={launchForm.description}
                  onChange={(e) => setLaunchForm({ ...launchForm, description: e.target.value })}
                />
              </div>

              <div className="userdashboard-launch-field">
                <label>Logo URL</label>
                <input
                  type="text"
                  placeholder="https://example.com/logo.png"
                  value={launchForm.logo_url}
                  onChange={(e) => setLaunchForm({ ...launchForm, logo_url: e.target.value })}
                />
              </div>

              <div className="userdashboard-launch-row">
                <div className="userdashboard-launch-field">
                  <label>Contact Person</label>
                  <input
                    type="text"
                    placeholder="Full name"
                    value={launchForm.contact}
                    onChange={(e) => setLaunchForm({ ...launchForm, contact: e.target.value })}
                  />
                </div>
                <div className="userdashboard-launch-field">
                  <label>Email</label>
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={launchForm.email}
                    onChange={(e) => setLaunchForm({ ...launchForm, email: e.target.value })}
                  />
                </div>
              </div>

              <div className="userdashboard-launch-row">
                <div className="userdashboard-launch-field">
                  <label>Phone</label>
                  <input
                    type="tel"
                    placeholder="+234..."
                    value={launchForm.phone}
                    onChange={(e) => setLaunchForm({ ...launchForm, phone: e.target.value })}
                  />
                </div>
                <div className="userdashboard-launch-field">
                  <label>Website</label>
                  <input
                    type="text"
                    placeholder="www.example.com"
                    value={launchForm.website}
                    onChange={(e) => setLaunchForm({ ...launchForm, website: e.target.value })}
                  />
                </div>
              </div>

              <div className="userdashboard-launch-field">
                <label>Address</label>
                <input
                  type="text"
                  placeholder="Street address"
                  value={launchForm.address}
                  onChange={(e) => setLaunchForm({ ...launchForm, address: e.target.value })}
                />
              </div>

              <div className="userdashboard-launch-row">
                <div className="userdashboard-launch-field">
                  <label>Location</label>
                  <input
                    type="text"
                    placeholder="City, State"
                    value={launchForm.location}
                    onChange={(e) => setLaunchForm({ ...launchForm, location: e.target.value })}
                  />
                </div>
                <div className="userdashboard-launch-field">
                  <label>Social Media</label>
                  <input
                    type="text"
                    placeholder="@yourhandle"
                    value={launchForm.social_media}
                    onChange={(e) => setLaunchForm({ ...launchForm, social_media: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="userdashboard-launch-modal-footer">
              <button className="userdashboard-launch-cancel" onClick={closeLaunchModal}>
                Cancel
              </button>
              <button
                className="userdashboard-launch-submit"
                onClick={handleLaunchSubmit}
                disabled={launching}
              >
                {launching ? (
                  <>
                    <Loader size={12} className="userdashboard-spinning" />
                    Launching...
                  </>
                ) : (
                  <>
                    <Zap size={12} />
                    Launch Now
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========== DEAL VIEW MODAL ========== */}
      {showDealView.show && showDealView.deal && (
        <div className="userdashboard-dealview-overlay" onClick={closeDealView}>
          <div className="userdashboard-dealview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="userdashboard-dealview-header">
              <h2>Business Details</h2>
              <button onClick={closeDealView}>
                <X size={16} />
              </button>
            </div>
            <div className="userdashboard-dealview-body">
              {/* Hero section with logo and name */}
              <div className="userdashboard-dealview-hero">
                <div className="userdashboard-dealview-logo">
                  {showDealView.deal.logo_url ? (
                    <img
                      src={getThumbnailUrl(showDealView.deal.logo_url, 200, 75)}
                      alt={showDealView.deal.business_name || 'Business'}
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        const img = e.currentTarget;
                        if (img.dataset.fallback === 'true') return;
                        img.dataset.fallback = 'true';
                        img.src = getOriginalUrl(showDealView.deal?.logo_url || '');
                      }}
                    />
                  ) : (
                    <div className="userdashboard-dealview-logo-placeholder">
                      {(showDealView.deal.business_name || 'B').charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="userdashboard-dealview-title">
                  <h3>{showDealView.deal.business_name || 'Unnamed Business'}</h3>
                  <span className="userdashboard-dealview-category">
                    {getCategoryName(showDealView.categoryId || showDealView.deal.category || '')}
                  </span>
                  {showDealView.deal.owner_name && (
                    <span className="userdashboard-dealview-owner">
                      <User size={10} /> by {showDealView.deal.owner_name}
                    </span>
                  )}
                </div>
              </div>

              {/* Description */}
              {showDealView.deal.description && (
                <div className="userdashboard-dealview-section">
                  <h4><Info size={12} /> About</h4>
                  <p>{showDealView.deal.description}</p>
                </div>
              )}

              {/* Contact Information */}
              <div className="userdashboard-dealview-section">
                <h4><Phone size={12} /> Contact Information</h4>
                <div className="userdashboard-dealview-details">
                  {showDealView.deal.contact && (
                    <div className="userdashboard-dealview-detail">
                      <span>Contact Person:</span>
                      <span>{showDealView.deal.contact}</span>
                    </div>
                  )}
                  {showDealView.deal.phone && (
                    <div className="userdashboard-dealview-detail">
                      <span>Phone:</span>
                      <a href={`tel:${showDealView.deal.phone}`}>{showDealView.deal.phone}</a>
                    </div>
                  )}
                  {showDealView.deal.email && (
                    <div className="userdashboard-dealview-detail">
                      <span>Email:</span>
                      <a href={`mailto:${showDealView.deal.email}`}>{showDealView.deal.email}</a>
                    </div>
                  )}
                  {showDealView.deal.website && (
                    <div className="userdashboard-dealview-detail">
                      <span>Website:</span>
                      <a href={`https://${showDealView.deal.website.replace(/^https?:\/\//, '')}`} target="_blank" rel="noreferrer">
                        {showDealView.deal.website}
                      </a>
                    </div>
                  )}
                  {showDealView.deal.social_media && (
                    <div className="userdashboard-dealview-detail">
                      <span>Social Media:</span>
                      <span>{showDealView.deal.social_media}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Location */}
              {(showDealView.deal.address || showDealView.deal.location) && (
                <div className="userdashboard-dealview-section">
                  <h4><MapPin size={12} /> Location</h4>
                  <div className="userdashboard-dealview-details">
                    {showDealView.deal.address && (
                      <div className="userdashboard-dealview-detail">
                        <span>Address:</span>
                        <span>{showDealView.deal.address}</span>
                      </div>
                    )}
                    {showDealView.deal.location && (
                      <div className="userdashboard-dealview-detail">
                        <span>Location:</span>
                        <span>{showDealView.deal.location}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showCancelConfirm.show && showCancelConfirm.orderItem && (
        <div className="userdashboard-modal-overlay">
          <div className="userdashboard-confirm-modal">
            <div className="userdashboard-confirm-header">
              <AlertTriangle size={16} color="#f59e0b" />
              <h3>Cancel Order</h3>
              <button onClick={() => setShowCancelConfirm({show: false, reason: ''})}>
                <X size={16} />
              </button>
            </div>
            <div className="userdashboard-confirm-body">
              <p>Are you sure you want to cancel this order?</p>
              <div className="userdashboard-order-details-confirm">
                <div><strong>Product:</strong> {showCancelConfirm.orderItem.product_title}</div>
                <div><strong>Unit Price:</strong> {formatCurrency(showCancelConfirm.orderItem.unit_price)}</div>
                <div><strong>Quantity:</strong> {showCancelConfirm.orderItem.quantity}</div>
                <div><strong>Total:</strong> {formatCurrency(showCancelConfirm.orderItem.total_price)}</div>
              </div>
              <p className="userdashboard-confirm-note">
                <Info size={12} color="#f59e0b" />
                Funds will be returned to your account from vendor's pending balance.
              </p>
              <div className="userdashboard-cancel-reason">
                <label>Reason for cancellation (required):</label>
                <select
                  value={showCancelConfirm.reason}
                  onChange={(e) => setShowCancelConfirm({...showCancelConfirm, reason: e.target.value})}
                >
                  <option value="">Select a reason</option>
                  {cancelReasons.map(reason => (
                    <option key={reason} value={reason}>{reason}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="userdashboard-confirm-actions">
              <button
                className="userdashboard-confirm-btn no"
                onClick={() => setShowCancelConfirm({show: false, reason: ''})}
              >
                No, Keep Order
              </button>
              <button
                className="userdashboard-confirm-btn yes"
                onClick={() => handleCancelOrder(showCancelConfirm.orderItem!, showCancelConfirm.reason)}
                disabled={!showCancelConfirm.reason.trim() || processingAction?.startsWith('cancel')}
              >
                {processingAction?.startsWith('cancel') ? (
                  <>
                    <Loader size={12} className="userdashboard-spinning" />
                    Cancelling...
                  </>
                ) : (
                  'Yes, Cancel Order'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showReceiveConfirm.show && showReceiveConfirm.orderItem && (
        <div className="userdashboard-modal-overlay">
          <div className="userdashboard-confirm-modal">
            <div className="userdashboard-confirm-header">
              <CheckCircle size={16} color="#10b981" />
              <h3>Receive Order</h3>
              <button onClick={() => setShowReceiveConfirm({show: false})}>
                <X size={16} />
              </button>
            </div>
            <div className="userdashboard-confirm-body">
              <p>Are you sure you have received this order?</p>
              <div className="userdashboard-order-details-confirm">
                <div><strong>Product:</strong> {showReceiveConfirm.orderItem.product_title}</div>
                <div><strong>Unit Price:</strong> {formatCurrency(showReceiveConfirm.orderItem.unit_price)}</div>
                <div><strong>Quantity:</strong> {showReceiveConfirm.orderItem.quantity}</div>
                <div><strong>Total:</strong> {formatCurrency(showReceiveConfirm.orderItem.total_price)}</div>
              </div>
              <p className="userdashboard-confirm-note">
                <AlertTriangle size={12} color="#f59e0b" />
                Funds will be transferred to vendor's available balance and cannot be reversed.
              </p>
            </div>
            <div className="userdashboard-confirm-actions">
              <button
                className="userdashboard-confirm-btn no"
                onClick={() => setShowReceiveConfirm({show: false})}
              >
                Not Yet
              </button>
              <button
                className="userdashboard-confirm-btn yes"
                onClick={() => handleReceiveOrder(showReceiveConfirm.orderItem!)}
                disabled={processingAction?.startsWith('receive')}
              >
                {processingAction?.startsWith('receive') ? (
                  <>
                    <Loader size={12} className="userdashboard-spinning" />
                    Processing...
                  </>
                ) : (
                  'Yes, I Received It'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showReview.show && showReview.orderItem && (
        <div className="userdashboard-modal-overlay">
          <div className="userdashboard-review-modal">
            <div className="userdashboard-review-header">
              <StarIcon size={16} color="#f59e0b" />
              <h3>Rate Your Purchase</h3>
              <button onClick={() => setShowReview({show: false})}>
                <X size={16} />
              </button>
            </div>
            <div className="userdashboard-review-body">
              <p>Please rate <strong>{showReview.orderItem.product_title}</strong></p>

              <div className="userdashboard-rating-section">
                <label>Rating:</label>
                <div className="userdashboard-stars">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      className={`userdashboard-star-btn ${star <= reviewData.rating ? 'active' : ''}`}
                      onClick={() => setReviewData({...reviewData, rating: star})}
                    >
                      <StarIcon size={20} fill={star <= reviewData.rating ? '#f59e0b' : 'none'} />
                    </button>
                  ))}
                </div>
                <div className="userdashboard-rating-presets">
                  {reviewPresets.map(preset => (
                    <button
                      key={preset.rating}
                      className={`userdashboard-preset-btn ${reviewData.rating === preset.rating ? 'active' : ''}`}
                      onClick={() => setReviewData({rating: preset.rating, review: preset.text})}
                    >
                      {preset.text}
                    </button>
                  ))}
                </div>
              </div>

              <div className="userdashboard-review-input">
                <label>Your Review:</label>
                <textarea
                  value={reviewData.review}
                  onChange={(e) => setReviewData({...reviewData, review: e.target.value})}
                  placeholder="Share your experience..."
                  rows={3}
                />
              </div>
            </div>
            <div className="userdashboard-review-actions">
              <button
                className="userdashboard-review-submit"
                onClick={handleSubmitReview}
                disabled={!reviewData.review.trim() || processingAction?.startsWith('review')}
              >
                {processingAction?.startsWith('review') ? (
                  <>
                    <Loader size={12} className="userdashboard-spinning" />
                    Submitting...
                  </>
                ) : (
                  'Submit Review'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {toastMessage && (
        <div className="userdashboard-toast">
          <CheckCircle size={14} />
          {toastMessage}
        </div>
      )}

      {featureUnavailable && (
        <div className="userdashboard-feature-modal">
          {featureUnavailable} not found check back later.<br />
          Contact support.
        </div>
      )}
    </div>
  );
};

export default UserDashboard;